'use client';

// Everything the console knows, held above the routes.
//
// This was the top half of one 2,800-line component that was the whole app. It
// is a provider now for one reason: a turn is followed by a loop that lives in
// refs — an SSE cursor, two abort controllers, the queue — and moving between
// two conversations, or out to a document and back, must not interrupt it.
// Next's layouts are documented to preserve state across navigation and pages
// are not, so anything that has to survive a route change lives here and the
// routes read it through `useConsole`.
//
// What did *not* move here is anything the URL can say instead. Which document
// is open, which conversation, whether the sidebar is showing the wiki — all of
// that used to be state and is now the address, which is why `mode`, `pane`,
// `openNode` and the session bookmark in localStorage are all gone rather than
// relocated.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { usePathname, useRouter } from 'next/navigation';
import type {
  AnalysisStatus,
  RepoKind,
  Session,
  SessionSummary,
  Turn as LiveTurn,
  WikiEntry,
  Workspace,
} from '@/lib/runner';
import { DEFAULT_MODEL, isModel, type Model } from '@/lib/models';
import { buildTree, RESEARCH_WORKSPACE, type TreeNode } from '@/lib/wiki';
import { childNamed, isProject, parseProject } from '@/lib/project';
import type { BoardEntry } from '@/lib/board';
import { clearToken, getServerSnapshot, getSnapshot, subscribe } from '@/lib/token-store';
import { morph } from './chats/morph';

// The shared password lives in localStorage rather than sessionStorage so a
// reload doesn't re-prompt; it is cleared the moment the server rejects it, so a
// rotated password can't leave a stale one sitting here.

// There used to be a `console.session` key here, holding which conversation
// this browser had open so a reload could land back on it. The address bar does
// that now, and does it better: a conversation can be linked, opened in two
// tabs, and reached with the back button, none of which a single key in
// localStorage could ever have managed.

// How often an open project page checks on a reanalysis in progress. Short
// enough that "running" doesn't sit stale for long, long enough that it costs
// nothing next to the turn itself, which runs for minutes.
const ANALYSIS_POLL_MS = 4000;

// The queue, which unlike everything else on screen exists nowhere but here.
// A message waiting for a turn to end is not a thing the runner has been told
// about, so if this browser forgets it, it is gone — and the browser forgets on
// every hot reload, on a crash, and whenever a phone decides a background tab
// has had long enough. Typed words are not something to lose to any of that.
//
// One entry per session, keyed by session id, because a queue now belongs to a
// thread and several threads are open at once. It was a single `console.queue`
// holding `{session, items, held}` back when opening another conversation
// emptied the only queue there was; that key is not read any more and a stale
// one left by an older build is simply ignored.
//
// Keyed by session rather than by thread, because a thread's name is this
// browser's own and means nothing after a reload — and a queue can only exist
// on a thread that has a session anyway, since queueing needs a turn to be
// running and running a turn is what creates one.
const QUEUES_KEY = 'console.queues';

// The rail: which conversations this browser is holding, in what order, which
// of them are windows rather than pills, and which one the pane is showing.
//
// All of that lived in the address for a while — `chat`, `chatspace`, `open`,
// `folded` — and the whole of it has moved here. The address was the wrong
// shape for it in a way that got worse the more the rail could do. A URL
// describes one thing you are looking at; the rail is eight conversations, an
// unsent draft and a stacking order, and encoding that as query params meant
// every fold rewrote the address, every navigation had to carry the rail
// forward by hand, and the back button walked through panel flips. What is
// left in the address now is the document you are reading, which is the one
// thing on screen worth linking to.
//
// Session ids rather than thread keys, same as the queue stash below: a thread
// key is this page load's own name for something and means nothing to the next
// one.
const WINDOWS_KEY = 'console.windows';

// Who is signed in, as the server reports them — /api/me, resolved from the
// password this browser holds.
//
// This was a hardcoded label with a comment explaining that it was a label and
// not an identity, because one password reached one runner and the app had no
// way to tell one browser from another. It is an identity now: the name decides
// which runner every request goes to, which workspaces exist, and whose sessions
// are listed. `wiki_workspace` is the one piece the browser needs for itself.
export type Me = {
  name: string;
  label: string;
  /** Empty until onboarding has made one. */
  wiki_workspace: string;
  /** Null until the register question has been answered — see PublicUser. */
  technical: boolean | null;
  onboarded: boolean;
};

// Reconnect backoff, and how many failures in a row before we stop trying and
// say so. The turn itself is unaffected either way; giving up only means this
// browser stops watching, and a reload will pick it back up.
const RETRY_CEILING_MS = 8000;
const MAX_RETRIES = 8;

export type Turn = {
  role: 'you' | 'claude';
  text: string;
  tools?: string[];
  meta?: { duration_ms?: number; cost_usd?: number };
  failed?: boolean;
  /**
   * Whether prose is arriving *right now*, as opposed to the turn merely being
   * open. A turn alternates: write a paragraph, run a tool, write another. Only
   * the marker at the end of the text cares about the difference — a light
   * hanging in the air while it works, a cursor while it writes — and nothing
   * else in a turn changes between the two, so this is the whole of the state.
   *
   * Set by `delta`, cleared by `tool`. Absent means not writing, which is right
   * for a turn that has just opened and for every turn read back from the
   * transcript.
   */
  writing?: boolean;
};

/**
 * A message typed while a turn was still running. It is held in this browser and
 * nowhere else — the runner takes one turn at a time, so a queued message is not
 * a thing the server knows about until its turn comes.
 *
 * The id exists so a row can be removed by identity rather than by index, which
 * is the whole point of the queue: the one you delete is the one that goes.
 */
type Queued = { id: number; text: string };

/** The queue itself: what is waiting, and whether it is paused. */
type Pending = { items: Queued[]; held: boolean };

/**
 * One conversation the console is holding open.
 *
 * This was a dozen separate `useState` calls sitting beside each other, which is
 * the same thing as this record for exactly one conversation and nothing at all
 * for two: a second thread would have overwritten the first's turns, its queue,
 * its model and whatever was half-typed into it. Gathering them is what lets
 * there be a list — see `threads`.
 *
 * `key` is this browser's own name for the thread and is deliberately not the
 * session id. A conversation exists here before the runner has heard of it, and
 * being given an id part-way through must not make it a different thread.
 */
export type Thread = {
  key: string;
  /** Null until the first message creates it — see `send`. */
  session: Session | null;
  chosen: Workspace[];
  model: Model;
  turns: Turn[];
  restoredCount: number;
  /** What is typed into the composer but not yet sent or queued. */
  draft: string;
  queue: Pending;
  busy: boolean;
  reconnecting: boolean;
  modelError: string | null;
  pulse: number;
  /**
   * A turn ended in here while you were reading something else.
   *
   * The whole point of holding several conversations at once is not having to
   * check them, so a tab has to be able to say "this one is done" without being
   * opened. Set when a turn finishes in a thread that is not in front, cleared
   * the moment it is — which is the honest reading of "unread": not that the
   * words are new, but that nobody has looked since they arrived.
   */
  unread: boolean;
};

/**
 * What follows a thread's turn, which is refs rather than state for the reason
 * the file header gives: the follow loop reads and writes this between awaits,
 * and a render is far too slow to be what it reads.
 *
 * One per thread, held in a Map. A single set of these is precisely what made
 * watching a second conversation stop watching the first — `detach` aborted the
 * only controller there was, so the turn you walked away from kept running on
 * the runner with nobody reading it.
 *
 * `sessionId` is here as well as on the Thread for the same reason `queue` is:
 * everything async needs it to build a URL, and state is one commit behind at
 * exactly the moments that matter. The Thread is what React renders; this is
 * what the loops read.
 */
type Live = {
  sessionId: string | null;
  watching: { turnId: string; cursor: number } | null;
  following: AbortController | null;
  link: AbortController | null;
  queue: Pending;
  nextQueued: number;
};

/**
 * Names for threads, handed out in order and never reused.
 *
 * Module scope rather than a ref: it only has to be unique within one page
 * load, and a counter that resets when the provider remounts would hand a new
 * thread the name of one still in the live map.
 */
let threadsNamed = 0;
const nameThread = () => `thread-${threadsNamed++}`;

/** Thrown when the server rejects the stored password. */
export class Unauthorized extends Error {}

/** Thrown when the turn we were watching is no longer on the runner. */
class TurnGone extends Error {}

type Frame = { seq: number | null; event: string; data: Record<string, unknown> };

/**
 * One SSE frame. Keepalive comments and anything unrecognised come back null.
 *
 * `id:` is the event's sequence number, and tracking it is what makes dropping
 * the connection survivable: reconnecting with the last one we saw picks the
 * turn up exactly where we stopped reading.
 */
function parseFrame(raw: string): Frame | null {
  let seq: number | null = null;
  let event = '';
  let data = '';
  for (const line of raw.split('\n')) {
    if (line.startsWith('id: ')) {
      const n = Number(line.slice(4));
      if (Number.isFinite(n)) seq = n;
    } else if (line.startsWith('event: ')) {
      event = line.slice(7).trim();
    } else if (line.startsWith('data: ')) {
      data = line.slice(6);
    }
  }
  if (!event) return null;
  try {
    return { seq, event, data: JSON.parse(data) as Record<string, unknown> };
  } catch {
    return null;
  }
}

/**
 * Sleep, waking early if the tab returns to the foreground or the network comes
 * back — the two things that make an immediate retry worth attempting. A phone
 * that has been in someone's pocket wakes up and reattaches at once rather than
 * sitting out the rest of a backoff it slept through.
 */
function sleepUntilAwake(ms: number): Promise<void> {
  return new Promise(resolve => {
    const finish = () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('online', onWake);
      resolve();
    };
    const onWake = () => {
      if (!document.hidden) finish();
    };
    const timer = setTimeout(finish, ms);
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('online', onWake);
  });
}

/** Nothing waiting. A value rather than a literal, because it is the answer to
 *  several different questions below and they should all give the same one. */
const NOTHING_QUEUED: Pending = { items: [], held: false };

// What the front-thread lookups below fall back to when nothing is in front.
// Shared constants rather than fresh literals, because these are read on every
// render and a new `[]` each time would be a new value to everything memoising
// on it — an empty list that changes identity is the most pointless re-render
// there is.
const NO_WORKSPACES: Workspace[] = [];
const NO_TURNS: Turn[] = [];

/**
 * The rail, as this browser will find it again.
 *
 * One conversation per entry, in the order they sit on the rail, each saying
 * whether it is a window or a pill. `front` is what the pane is showing —
 * a session id, or `DRAFT` for a chat nobody has sent to yet.
 *
 * Names rather than thread keys throughout, for the reason the queue stash
 * gives: `thread-4` is this page load's own word for something and means
 * nothing to the next one. A session id is the same conversation tomorrow.
 */
type StashedRail = {
  threads: { id: string; open: boolean }[];
  /** The unsent chat, if there is one, and which projects it was started with. */
  draft: { spaces: string[]; open: boolean } | null;
  front: string | null;
};

/** What `front` says when the pane is holding a chat with no session yet. */
const DRAFT = 'new';

const NO_RAIL: StashedRail = { threads: [], draft: null, front: null };

/** Which workspaces a stashed list of names is naming, dropping any that went. */
export function readDraftSpaces(names: string[], available: Workspace[]): Workspace[] {
  return names
    .map(name => available.find(w => w.name === name))
    .filter(Boolean) as Workspace[];
}

/**
 * The rail this browser left behind, or an empty one.
 *
 * Anything unreadable or written by an older build reads as nothing open —
 * the same rule the queue stash follows, and for the same reason: a rail is
 * worth restoring and never worth an error on the way into the app, which is
 * the one moment where throwing costs someone the whole console.
 */
export function stashedRail(): StashedRail {
  try {
    const raw = localStorage.getItem(WINDOWS_KEY);
    if (!raw) return NO_RAIL;
    const it = JSON.parse(raw) as Partial<StashedRail>;
    return {
      threads: (Array.isArray(it.threads) ? it.threads : []).filter(
        t => t && typeof t.id === 'string',
      ),
      draft:
        it.draft && Array.isArray(it.draft.spaces)
          ? { spaces: it.draft.spaces.filter(s => typeof s === 'string'), open: !!it.draft.open }
          : null,
      front: typeof it.front === 'string' ? it.front : null,
    };
  } catch {
    return NO_RAIL;
  }
}

/**
 * The queue this browser stashed, if it belongs to the session being opened.
 *
 * Anything unreadable, half-written, or left over from another conversation
 * reads as nothing waiting. A queue is worth restoring; it is never worth an
 * error on the way into a session, which is the one moment where throwing would
 * cost someone the conversation as well as the queue.
 */
function stashedQueue(sessionId: string): Pending {
  try {
    const raw = localStorage.getItem(QUEUES_KEY);
    if (!raw) return NOTHING_QUEUED;
    const all = JSON.parse(raw) as Record<string, { items?: Queued[]; held?: boolean }>;
    const saved = all?.[sessionId];
    if (!saved || !Array.isArray(saved.items)) return NOTHING_QUEUED;
    const items = saved.items.filter(
      item => typeof item?.id === 'number' && typeof item?.text === 'string',
    );
    return items.length ? { items, held: Boolean(saved.held) } : NOTHING_QUEUED;
  } catch {
    return NOTHING_QUEUED;
  }
}

/**
 * Whether the sidebar is a sidebar rather than a drawer — the same 720px the
 * stylesheet splits on, kept in one place here and one place there.
 *
 * The app decides layout in CSS wherever it can. This is the one thing CSS
 * cannot do: the workspace picker belongs in the drawer on a phone and in the
 * pane on a desktop, and moving a node between two parents is not something a
 * media query can express. The alternative was rendering it twice and hiding
 * one, which puts the same twelve buttons in the document twice.
 *
 * The server snapshot says desktop, so a phone's first paint disagrees for one
 * frame. It costs nothing: `pane` starts on 'chats', so nothing this decides is
 * on screen until someone has pressed a button, long after hydration.
 */
const WIDE = '(min-width: 721px)';

/**
 * Whether the client has taken over from the server render.
 *
 * The same `useSyncExternalStore` trick `useWide` uses above, with the store
 * removed: a subscription nothing ever fires, a client snapshot that is always
 * true and a server snapshot that is always false. React renders the server
 * answer through hydration and the client answer immediately after, which is
 * exactly the fact wanted — not "is there a token" but "has anybody looked".
 */
const neverChanges = () => () => {};

export function useHydrated(): boolean {
  return useSyncExternalStore(
    neverChanges,
    () => true,
    () => false,
  );
}

export function useWide() {
  return useSyncExternalStore(
    cb => {
      const q = window.matchMedia(WIDE);
      q.addEventListener('change', cb);
      return () => q.removeEventListener('change', cb);
    },
    () => window.matchMedia(WIDE).matches,
    () => true,
  );
}

/**
 * A ref that keeps a CSS variable equal to the element's height.
 *
 * Both pieces of floating chrome need this and neither has a height worth
 * hard-coding: the composer grows as a message is typed into it, the queue above
 * it grows a row at a time, and the header is two lines of text plus whatever
 * the device reserves for its own status bar. Measuring is the only honest
 * answer, and the stream reads the result as padding.
 *
 * A callback ref rather than an effect, because neither element is always
 * mounted — opening a wiki file takes the whole column, and the header is
 * `display: none` on a desktop, where this correctly reports zero. Measuring
 * starts when the element arrives and stops when it goes, with no dependency
 * list to keep in step with which pane is on screen.
 */
export function useMeasuredHeight(name: string, scoped = false) {
  return useCallback(
    (el: HTMLElement | null) => {
      if (!el) return;
      // `scoped` puts the answer on the element's own parent instead of on the
      // document. One composer could speak for the whole app; several cannot,
      // and a window's stream needs the height of *its* dock rather than of
      // whichever one measured itself last. The parent is the right home
      // because the stream is the dock's sibling and inherits from it.
      const root = scoped ? (el.parentElement ?? document.documentElement) : document.documentElement;
      // The margin box, not the border box. The header is inset from the edges
      // of the screen, so the space it takes out of the pane includes the gap it
      // is holding around itself — and that gap is partly `env(safe-area-inset)`,
      // which is a number only the device knows. The dock has no margins and is
      // unaffected either way.
      const measure = () => {
        const style = getComputedStyle(el);
        const margins = parseFloat(style.marginTop) + parseFloat(style.marginBottom);
        root.style.setProperty(name, `${el.offsetHeight + margins}px`);
      };
      measure();
      const watch = new ResizeObserver(measure);
      watch.observe(el);
      return () => {
        watch.disconnect();
        root.style.removeProperty(name);
      };
    },
    [name, scoped],
  );
}

function useConsoleState() {
  const router = useRouter();
  const pathname = usePathname();
  const token = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  // Every conversation this browser is holding open, in the order they were
  // opened, and which of them is in front.
  //
  // The list is new; everything on a `Thread` used to be a `useState` of its own
  // up here, which meant the console could hold exactly one conversation and
  // opening a second was really closing the first. The runner has never had that
  // limit — it keeps one live turn per session, each on its own thread — so this
  // was the whole of what stopped several conversations running at once.
  //
  // Nothing yet puts more than one in the list. What changes here is that it
  // *can* hold more, which is what everything below had to stop assuming first.
  const [threads, setThreads] = useState<Thread[]>([]);
  const [frontKey, setFrontKey] = useState<string | null>(null);
  // The wiki, once fetched. Null means "not asked for yet" rather than "empty".
  // It used to be loaded the first time the wiki tab was opened; the board is
  // the front door now and reads the same tree, so it is fetched with everything
  // else and the laziness bought nothing.
  const [tree, setTree] = useState<TreeNode[] | null>(null);
  const [treeError, setTreeError] = useState<string | null>(null);
  /*
   * The research shelf, on the same terms — except that its failures are silent
   * and the wiki's are not.
   *
   * The wiki reports because a pane that stays empty is indistinguishable from a
   * repo with nothing in it, and the wiki always has something in it. Research
   * genuinely might not exist: the workspace is one row in a registry the runner
   * reads once at startup, so between adding the row and restarting the runner
   * every request for it is a legitimate 404. A banner for that would be the
   * sidebar shouting about a repo most people have not made yet.
   *
   * So null means "no shelf", whether that is because nothing answered or
   * because there is nothing there, and the section simply is not drawn.
   */
  const [research, setResearch] = useState<TreeNode[] | null>(null);
  // Bumped once a turn that edited a file finishes, so an open document refetches
  // even though its own key — space and path — never changed. Read only as "which
  // version is this", same shape as `pulse`.
  const [wikiVersion, setWikiVersion] = useState(0);
  // Which directories are open — root-level ones no longer ask, see
  // `WikiRows`, so this now only ever matters for a directory nested deeper
  // than the wiki root, which nothing currently produces.
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  // Every project's head, read once and kept — see `loadProjects`. Null is "not
  // asked for yet", which the board draws as its skeleton.
  const [projects, setProjects] = useState<BoardEntry[] | null>(null);
  // Which project directories are fixtures, by path — see `Project.fixture`. The
  // sidebar draws its rows from the tree, which carries commit ages and names
  // and no frontmatter at all, so the one place that reads a project's head has
  // to hand this down. Empty until the heads land, which means a fixture is on
  // screen for the moment between the tree arriving and the reads finishing.
  const [fixtures, setFixtures] = useState<Set<string>>(() => new Set());
  const [projectsError, setProjectsError] = useState<string | null>(null);
  // Which session's forget button has been armed. A second click confirms.
  const [arming, setArming] = useState<string | null>(null);
  // Who the server says this password belongs to. Null until /api/me answers,
  // which is the first thing the load effect below asks for — everything after
  // it is per-user, so there is nothing useful to do before it lands.
  const [me, setMe] = useState<Me | null>(null);
  // The wiki's workspace for this user. Empty before /api/me answers; every use
  // of it below is either a comparison (safe) or gated on `me` being there.
  const wikiSpace = me?.wiki_workspace ?? '';
  const [accountOpen, setAccountOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /*
   * Whether there is anything worth putting on screen yet. The veil in ./boot is
   * up until this turns true, and `Shell` renders nothing else while it is.
   *
   * Three states collapsed into two, and the collapse is the point. Before
   * hydration nobody has read localStorage, so "no token" is a default rather
   * than a finding — showing the gate on the strength of it is what made every
   * reload of an unlocked browser flash a password prompt. After hydration a
   * browser with no token is ready at once, because the gate is the whole of
   * what it is going to get; one with a token waits for /api/me, which is the
   * first request the load effect below makes and the one everything after it
   * is keyed to. An error counts as ready — the shell draws the banner, and a
   * mark shining over a failure is the interface lying about what happened.
   */
  const hydrated = useHydrated();
  const ready = hydrated && (!token || me !== null || error !== null);
  // Narrow screens hide the sidebar off-canvas; this opens it. Desktop ignores
  // it entirely — the sidebar is always there and the drawer chrome is display:none.
  const [menuOpen, setMenuOpen] = useState(false);
  /**
   * Which conversations are expanded — a pill on the rail that has grown into a
   * window. Several at once on a desktop; on a phone, one.
   *
   * This was a single `chatOpen` boolean, which is what "expanded" means when
   * there is one panel and it is either up or down. There are as many panels as
   * there are threads now, so the question stopped being yes-or-no and became
   * which — and the phone's limit of one is a rule applied to this list rather
   * than a different shape of state, so both layouts render from the same thing.
   *
   * Not an address, and neither is the archive below: they are which panels are
   * up, the same kind of fact as a drawer being open, and putting them in the
   * URL would make the back button walk through panel flips.
   */
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  /** The saved-conversations list, which is a panel of the same kind. */
  const [archiveOpen, setArchiveOpen] = useState(false);
  // Choosing where a new conversation will sit used to be a third face of this
  // panel, shown *instead* of the composer: you picked a workspace and only then
  // got somewhere to type. That is gone. The cards are part of a new chat now,
  // above the field, and picking none is a legal way to start — so there is no
  // state for "still deciding", because deciding is no longer a step you have to
  // finish before you can say anything.
  // Whether anything has passed under the header — the same question
  // fian-website's nav asks, and the reason its rest state can be nothing at
  // all. At the top of a pane the stream's own top padding clears the header,
  // so there is genuinely no text behind it and it can stop being an object;
  // one pixel of scroll and there is, so it becomes one.
  //
  // Distance from the top, not from the live edge at the bottom. A conversation
  // opens at the bottom, which reads like a resting position and is not one:
  // there is a full screen of history under the bar there, and a transparent
  // header over it put the title straight onto someone's message.
  //
  // Stamped with the route it was measured on, so moving to another one reads as
  // "not scrolled" without anything having to reset it. Every route arrives at
  // its top, so that is always the truth — and a derivation cannot be one render
  // behind the way an effect that clears this would be.
  const [scrolled, setScrolled] = useState({ path: '', on: false });
  // Bumped to re-read everything from the server. The one case that needs it is
  // a turn we were watching disappearing (the runner restarted under us), where
  // the transcript is the only remaining truth.
  const [resync, setResync] = useState(0);

  const menuButton = useRef<HTMLButtonElement>(null);

  /**
   * Every open composer, by thread.
   *
   * There used to be one `input` ref here, because there was one composer. With
   * several conversations expanded at once there are several, and "put the
   * cursor back in the field" has to name which field — so each `Conversation`
   * lends its textarea to this map while it is mounted and takes it back when
   * it goes. The map, not the component, is what the provider can reach.
   */
  const inputs = useRef(new Map<string, HTMLTextAreaElement>());

  // A thread whose composer was asked for before it existed. `beginDraft` wants
  // the cursor in a field that React has not mounted yet, so the wish is kept
  // here and granted by the field itself the moment it arrives.
  const wantsFocus = useRef<string | null>(null);

  const lendInput = useCallback((key: string, el: HTMLTextAreaElement | null) => {
    if (!el) return void inputs.current.delete(key);
    inputs.current.set(key, el);
    if (wantsFocus.current === key) {
      wantsFocus.current = null;
      el.focus();
    }
  }, []);

  /** Focus a thread's composer, now if it is on screen and otherwise on arrival. */
  const focusThread = useCallback((key: string) => {
    const el = inputs.current.get(key);
    if (el) el.focus();
    else wantsFocus.current = key;
  }, []);

  // Both ends of the conversation float over it, and the conversation has to
  // know how much of itself is underneath each of them — see `.stream`, which
  // spends both of these as padding so that nothing at rest is ever covered.
  const dock = useMeasuredHeight('--dock-height');
  const topbar = useMeasuredHeight('--topbar-height');

  // What follows each thread's turn — see `Live`. Keyed by thread, so a turn
  // running in one conversation is not something opening another can interrupt.
  const live = useRef(new Map<string, Live>());

  /** This thread's machinery, made on first use so no caller has to create it. */
  const liveFor = useCallback((key: string): Live => {
    let it = live.current.get(key);
    if (!it) {
      it = {
        sessionId: null,
        watching: null,
        following: null,
        link: null,
        queue: NOTHING_QUEUED,
        nextQueued: 0,
      };
      live.current.set(key, it);
    }
    return it;
  }, []);

  /**
   * Mirror every thread's queue to storage, so that losing the page is not the
   * same as losing what was typed into it.
   *
   * Read off the live records rather than off state, and called rather than
   * watched. It used to be an effect on the one queue there was; an effect on
   * *every* thread's queue would have to depend on the whole thread list, which
   * changes on every token of every streaming reply — a JSON write per delta,
   * to say nothing new about the queue almost every time.
   *
   * Nothing is written for a thread with no session yet, which is also what
   * stops the empty queue a fresh thread starts with from erasing the stash it
   * is about to restore from. The session a queue belongs to is not always known
   * at the moment the queue changes — the first message of a conversation
   * creates the session while a second one is already queueing behind it — so
   * `send` calls this again the moment the id lands.
   */
  const stashQueues = useCallback((forget?: string) => {
    // Merged into what is already there rather than written over it. This knows
    // about the threads that are open, and a stash can outlive one — a reload
    // brings back the conversation the address names and not yet the rest, so
    // rewriting the file from the open threads alone would throw away the queue
    // belonging to every conversation that had not been reopened. Whose queue
    // it is, is not this function's to decide; it only knows whose it has.
    let all: Record<string, Pending> = {};
    try {
      all = (JSON.parse(localStorage.getItem(QUEUES_KEY) ?? '{}') ?? {}) as Record<string, Pending>;
    } catch {
      all = {};
    }
    // A thread that has been closed, named explicitly because its live record
    // has already gone — see `dropThread`.
    if (forget) delete all[forget];
    for (const it of live.current.values()) {
      if (!it.sessionId) continue;
      if (it.queue.items.length) all[it.sessionId] = it.queue;
      else delete all[it.sessionId];
    }
    if (!Object.keys(all).length) return localStorage.removeItem(QUEUES_KEY);
    localStorage.setItem(QUEUES_KEY, JSON.stringify(all));
  }, []);

  /**
   * Stop following a thread for good and forget what was held for it.
   *
   * The stash goes too, and this is the only place it does: closing a thread is
   * the one act that means "I am not coming back to this" — walking away to
   * another conversation now leaves everything exactly where it was.
   */
  const dropThread = useCallback(
    (key: string) => {
      const it = live.current.get(key);
      it?.following?.abort();
      live.current.delete(key);
      stashQueues(it?.sessionId ?? undefined);
    },
    [stashQueues],
  );

  // The list as the callbacks below see it, rather than as React is rendering
  // it. Leaving a conversation has to know what it is leaving, and an event
  // handler that closed over `threads` would put the whole list in the
  // dependencies of half the functions in this file — every keystroke of a
  // streaming reply changes it.
  const threadsNow = useRef<Thread[]>([]);
  useEffect(() => {
    threadsNow.current = threads;
  }, [threads]);

  // Same reason as `threadsNow`: raising a window has to know what is already
  // open right now, not what the last render says, and the rail is written down
  // from that same up-to-date row — see `stashRail`.
  const openKeysNow = useRef<string[]>([]);
  useEffect(() => {
    openKeysNow.current = openKeys;
  }, [openKeys]);

  // Counts loads of a conversation, so the one that ends up in front is the one
  // asked for last rather than the one whose transcript arrived last — see
  // `openSession`.
  const opening = useRef(0);

  // Which session ids have a load in flight. A different question from "which
  // are open", and the one that stops the same conversation being fetched twice
  // when a restore asks for the rail and the pane in the same tick — see
  // `openSession`.
  const loading = useRef(new Set<string>());

  // Where on the rail each restored conversation belongs, by session id.
  // Filled by the restore in `shell.tsx` and read by `stashRail`, which is the
  // only thing that needs it: on screen the row is drawn from `threads`, and
  // getting *that* into order would mean holding the whole restore back until
  // the slowest transcript landed. Writing the right order down instead costs
  // nothing and is what the next reload reads.
  const railOrder = useRef(new Map<string, number>());
  const rankOf = useCallback(
    (id: string | undefined) => railOrder.current.get(id ?? '') ?? Number.MAX_SAFE_INTEGER,
    [],
  );

  /**
   * A thread into a list, at the place the rail says it belongs.
   *
   * Scans back over anything that ranks after it, which for an unranked thread
   * — one opened by hand, rather than restored — is nothing at all, so it
   * lands on the end exactly as `[...all, t]` used to put it.
   */
  const insertByRank = useCallback(
    (all: Thread[], t: Thread) => {
      const rank = rankOf(t.session?.session_id);
      const next = [...all];
      let i = next.length;
      while (i > 0 && rankOf(next[i - 1].session?.session_id) > rank) i--;
      next.splice(i, 0, t);
      return next;
    },
    [rankOf],
  );

  // Which thread is in front, where the follow loop can read it. A turn ending
  // has to know whether anyone was looking, and the loop that knows a turn
  // ended runs between awaits — see `Thread.unread`.
  //
  // Written by `setFront` rather than only mirrored by an effect: the rail is
  // stashed from this ref in the same handler that moves the pane, and a mirror
  // that lands one commit later would record the conversation you just left.
  const frontNow = useRef<string | null>(null);

  // `showChat` reaches `openSession`, which is declared several hundred lines
  // below it. A ref rather than a reorder: `openSession` closes over half this
  // file, and hoisting it above the archive's handler would drag the load
  // machinery up with it.
  const openSessionRef = useRef<(id: string, opts?: { focus?: boolean; unfold?: boolean }) => Promise<void>>(
    async () => {},
  );

  /**
   * Change one thread. The only writer, so nothing else has to find it first.
   *
   * Returns the list unchanged when the thread is gone or the change is a
   * no-op, rather than mapping a fresh array either way. With one conversation
   * that was free; with eight, and a patch landing per token of every streaming
   * reply, a new array identity is every tab re-rendering to say nothing.
   */
  const patchThread = useCallback((key: string, fn: (t: Thread) => Thread) => {
    setThreads(all => {
      const at = all.findIndex(t => t.key === key);
      if (at < 0) return all;
      const next = fn(all[at]);
      if (next === all[at]) return all;
      const copy = all.slice();
      copy[at] = next;
      return copy;
    });
  }, []);

  // Looking at a conversation is what marks it read. An effect rather than a
  // line in every function that changes which thread is in front, because there
  // are five of those and this is a fact about the result, not about the route
  // taken to it.
  useEffect(() => {
    if (frontKey) patchThread(frontKey, t => (t.unread ? { ...t, unread: false } : t));
  }, [frontKey, patchThread]);

  // Which conversation is on screen, and everything the rest of the app reads
  // off it. These used to be the state itself; they are a lookup now, and the
  // names are unchanged so that nothing outside this file had to learn about
  // threads to keep working.
  const front = threads.find(t => t.key === frontKey) ?? null;
  // Whether the pane is holding a conversation at all — which is now simply
  // whether there is one in front of you.
  const drafting = front !== null;
  const chosen = front?.chosen ?? NO_WORKSPACES;
  const session = front?.session ?? null;
  const model = front?.model ?? DEFAULT_MODEL;
  const turns = front?.turns ?? NO_TURNS;
  const restoredCount = front?.restoredCount ?? 0;
  const draft = front?.draft ?? '';
  const queue = front?.queue ?? NOTHING_QUEUED;
  const busy = front?.busy ?? false;
  const reconnecting = front?.reconnecting ?? false;
  const modelError = front?.modelError ?? null;
  const pulse = front?.pulse ?? 0;

  /**
   * The only writer to a thread's queue. Ref first, then the render copy, so
   * they cannot disagree — see `Live`.
   */
  const updateQueue = useCallback(
    (key: string, fn: (q: Pending) => Pending) => {
      const it = liveFor(key);
      it.queue = fn(it.queue);
      patchThread(key, t => ({ ...t, queue: it.queue }));
      stashQueues();
    },
    [liveFor, patchThread, stashQueues],
  );

  /** Pause a thread's queue. Nothing is lost; it stops going out by itself. */
  const hold = useCallback(
    (key: string) => {
      updateQueue(key, q => (q.held ? q : { ...q, held: true }));
    },
    [updateQueue],
  );

  // What to do when a turn finishes: send the next queued message. Late-bound
  // through a ref because the two halves point at each other — the follow loop
  // below ends a turn, and the end of a turn starts a message, which is sent by
  // `send`, which follows the turn it starts. See `drain`.
  const turnEnded = useRef<(key: string) => void>(() => {});

  /**
   * Stop watching. The turn carries on regardless; this is a local decision.
   *
   * Clearing `busy` here is what makes it safe to walk away from a running turn
   * — switch to another session, come back later. The follow loop's own cleanup
   * won't do it, because by then it has been superseded and deliberately
   * declines to touch state a newer watcher may own.
   */
  const detach = useCallback(
    (key: string) => {
      const it = live.current.get(key);
      if (it) {
        it.following?.abort();
        it.following = null;
        it.link = null;
        it.watching = null;
      }
      patchThread(key, t => ({ ...t, busy: false, reconnecting: false }));
    },
    [patchThread],
  );

  // There used to be a `clearQueue` here, called by everything on its way out of
  // a conversation. A queue belongs to a thread now and goes when the thread
  // does — `dropThread` takes the live record with it, and the record is where
  // the queue lives — so the four callers each say what they actually mean
  // instead of sharing one function that meant "empty the only queue there is".

  /** Shut the drawer, handing focus back to the button that opened it. */
  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    menuButton.current?.focus();
  }, []);

  /**
   * Whether anything is expanded at all. What the phone's header button and the
   * shell's own `is-chat-open` are asking, and the one question the old boolean
   * was still answering correctly.
   */
  const chatOpen = openKeys.length > 0 || archiveOpen;

  /**
   * Raise a conversation, or the archive, into a window.
   *
   * A desktop adds to what is already up; a phone replaces it. That is the only
   * place the two layouts differ, and it is here rather than in the CSS because
   * it is a rule about state — a phone cannot show two windows at once, so it
   * must not be able to be in a state where two are open, or turning the phone
   * sideways would reveal one nobody asked for.
   */
  const wide = useWide();

  /**
   * How many windows fit beside the wiki before they start covering it.
   *
   * Measured rather than assumed: the sidebar's width is a clamp against the
   * viewport, so the only honest source for it is the element. The window's own
   * width comes off the token it is set from, for the same reason — two numbers
   * that have to agree, kept in one place each.
   *
   * At least one, always. A screen too narrow for a single window is a screen
   * that gets one anyway; covering the wiki is better than refusing to open the
   * conversation someone just asked for.
   */
  const roomForWindows = useCallback(() => {
    if (typeof window === 'undefined') return 1;
    const css = getComputedStyle(document.documentElement);
    const each = parseFloat(css.getPropertyValue('--chatwin')) || 360;
    const wiki = document.querySelector('.sidebar')?.getBoundingClientRect().width ?? 0;
    // What the two controls at the end of the rail take, plus the gaps.
    const tools = 170;
    return Math.max(1, Math.floor((window.innerWidth - wiki - tools) / (each + 8)));
  }, []);

  /**
   * Write the rail down, so that losing the page is not the same as losing what
   * was open on it.
   *
   * Read off the three refs rather than off state, and called rather than
   * watched — the same shape as `stashQueues`, and for the same two reasons.
   * An effect depending on `threads` would run on every token of every
   * streaming reply, writing JSON to say nothing new about the rail almost
   * every time; and an effect could only ever run *after* the render that
   * changed things, which on the first render of a reload is a rail that is
   * still empty. Syncing that to storage would erase the very thing the reload
   * exists to read back, before the restore in `shell.tsx` gets a chance to.
   *
   * Every caller therefore sets the refs synchronously and calls this. That is
   * why `setFront` below exists: `frontKey` is the one of the three that used
   * to be state alone, and a stash written from a mirror one commit behind
   * would name the conversation you just left.
   */
  const stashRail = useCallback(() => {
    const open = openKeysNow.current;
    const draft = threadsNow.current.find(t => !t.session);
    const here = threadsNow.current
      .filter(t => t.session)
      .map(t => ({ id: t.session!.session_id, open: open.includes(t.key) }));

    // Anything still on its way in keeps its place. A restore asks for the
    // whole rail at once and every conversation writes the rail again as it
    // lands, so without this the first one home would be the only one written
    // down — and a reload while a slow restore was still running would come
    // back holding just that one, having quietly dropped the rest.
    //
    // `loading` rather than "was in the old stash", which would be the same
    // thing right up until someone closed a conversation: a close has to be
    // able to take one off, and a merge that kept everything it found could
    // never let it.
    const held = new Set(here.map(t => t.id));
    const waiting = stashedRail().threads.filter(
      t => !held.has(t.id) && loading.current.has(t.id),
    );

    const rail: StashedRail = {
      // Sorted back into rail order, because a restore adds each conversation
      // as its transcript arrives and those come back in whatever order the
      // runner and the network between them decide. `railOrder` is what the
      // restore asked for; anything opened by hand since has no entry and
      // sorts to the end, which is where it is on screen.
      threads: [...here, ...waiting].sort((a, b) => rankOf(a.id) - rankOf(b.id)),
      draft: draft
        ? { spaces: draft.chosen.map(w => w.name), open: open.includes(draft.key) }
        : null,
      front: frontNow.current
        ? (threadsNow.current.find(t => t.key === frontNow.current)?.session?.session_id ??
          (threadsNow.current.some(t => t.key === frontNow.current) ? DRAFT : null))
        : null,
    };
    // Nothing held and nothing drafted is nothing to come back to, and a key
    // sitting there saying so would outlive the build that wrote it.
    if (!rail.threads.length && !rail.draft) return localStorage.removeItem(WINDOWS_KEY);
    localStorage.setItem(WINDOWS_KEY, JSON.stringify(rail));
  }, [rankOf]);

  /**
   * Move the pane, in the ref and the state at once.
   *
   * Both, always: `stashRail` reads the ref, and half the callers below stash
   * in the same handler that moves the pane.
   */
  const setFront = useCallback((key: string | null) => {
    frontNow.current = key;
    setFrontKey(key);
  }, []);

  const raiseThread = useCallback(
    (key: string) => {
      const open = openKeysNow.current;
      let next: string[];
      if (!wide) next = [key];
      else if (open.includes(key)) next = open;
      else {
        // The oldest gives way when there is no more room, which is what a row
        // of windows has to do rather than run off the side of the screen or
        // start covering the wiki. It is folded, not closed: its pill is back on
        // the rail and its turn never stopped.
        const room = Math.max(1, roomForWindows() - (archiveOpen ? 1 : 0));
        next = [...open, key].slice(-room);
      }
      // Set synchronously, not just via the effect that mirrors `openKeys` —
      // `stashRail` on the next line reads this ref to say what is open, and
      // the effect does not run until after this render commits.
      openKeysNow.current = next;
      setOpenKeys(next);
      if (!wide) setArchiveOpen(false);
      setFront(key);
      stashRail();
    },
    [wide, archiveOpen, roomForWindows, setFront, stashRail],
  );

  /** Put one back down. The thread stays open and stays followed. */
  const lowerThread = useCallback(
    (key: string) => {
      const next = openKeysNow.current.filter(k => k !== key);
      openKeysNow.current = next;
      setOpenKeys(next);
      stashRail();
    },
    [stashRail],
  );

  /**
   * The same as `raiseThread`, but for a window that is unfolding itself back
   * into place on a reload rather than being asked for. It joins the row
   * without taking `frontKey` from whichever one the stash named, which is
   * what keeps a reload from settling on the last thread that happened to
   * finish resuming rather than the one that was actually in front.
   */
  const raiseThreadBackground = useCallback(
    (key: string) => {
      if (!wide) return;
      const open = openKeysNow.current;
      if (open.includes(key)) return;
      const room = Math.max(1, roomForWindows() - (archiveOpen ? 1 : 0));
      const next = [...open, key].slice(-room);
      openKeysNow.current = next;
      setOpenKeys(next);
      stashRail();
    },
    [wide, archiveOpen, roomForWindows, stashRail],
  );

  const raiseArchive = useCallback(() => {
    setArchiveOpen(true);
    if (!wide) {
      openKeysNow.current = [];
      setOpenKeys([]);
      stashRail();
    }
  }, [wide, stashRail]);

  /** Everything down, which is what tapping beside a sheet means. */
  const closeChat = useCallback(() => {
    openKeysNow.current = [];
    setOpenKeys([]);
    stashRail();
    setArchiveOpen(false);
  }, [stashRail]);

  const lock = useCallback(
    (message?: string) => {
      // Every conversation, not just the one on screen. A locked console that
      // left a turn streaming into a thread behind the gate would be holding
      // someone else's work open on a screen they just secured.
      for (const it of live.current.values()) it.following?.abort();
      live.current.clear();
      localStorage.removeItem(QUEUES_KEY);
      // And the rail. It names conversations, which is the one thing on it
      // worth keeping from whoever unlocks this browser next — and leaving it
      // would put them back on screen the moment somebody did.
      localStorage.removeItem(WINDOWS_KEY);
      threadsNow.current = [];
      openKeysNow.current = [];
      setThreads([]);
      setOpenKeys([]);
      setFront(null);
      setMenuOpen(false);
      clearToken();
      setWorkspaces([]);
      setSessions([]);
      // Dropped rather than kept: the tree is server data behind the same
      // password as everything else, and leaving it on screen after a lock would
      // be the one thing still readable without one.
      setTree(null);
      setTreeError(null);
      setResearch(null);
      setProjects(null);
      setProjectsError(null);
      setArming(null);
      setAccountOpen(false);
      setError(message ?? null);
      // Back to the front door. The gate renders over whatever route you were
      // on, so this is not what hides the content — it is so that unlocking
      // again does not drop you straight back into a conversation you locked
      // the screen to get out of.
      router.push('/');
    },
    [router, setFront],
  );

  /** Only ever mutate the trailing turn: that is the one being streamed into. */
  const patch = useCallback(
    (key: string, fn: (t: Turn) => Turn) => {
      patchThread(key, th => ({
        ...th,
        turns: th.turns.map((t, i) => (i === th.turns.length - 1 ? fn(t) : t)),
      }));
    },
    [patchThread],
  );

  /** fetch + bearer token, turning a server rejection into a re-prompt. */
  const authFetch = useCallback(
    async (url: string, init?: RequestInit) => {
      const res = await fetch(url, {
        ...init,
        headers: { ...(init?.headers ?? {}), Authorization: `Bearer ${token ?? ''}` },
      });
      if (res.status === 401) throw new Unauthorized('Password rejected — enter it again.');
      return res;
    },
    [token],
  );

  /**
   * Read the wiki's file tree.
   *
   * Unlike the session list this reports its failures, because there is nothing
   * else on screen to fall back to: a wiki pane that silently stays empty is
   * indistinguishable from a wiki with nothing in it.
   */
  /** Returns what it built as well as setting it, so a caller can read on. */
  const loadTree = useCallback(async (): Promise<TreeNode[] | null> => {
    try {
      const res = await authFetch('/api/wiki/tree');
      const body = (await res.json()) as { entries?: WikiEntry[]; error?: string };
      if (!res.ok) throw new Error(body.error ?? 'could not read the wiki');
      const nodes = buildTree(body.entries ?? []);
      setTree(nodes);
      setTreeError(null);
      return nodes;
    } catch (e) {
      if (e instanceof Unauthorized) {
        lock(e.message);
        return null;
      }
      setTreeError((e as Error).message);
      return null;
    }
  }, [authFetch, lock]);

  /**
   * The same read against the research repo, which is allowed not to be there.
   *
   * Every failure lands in the same place — a 404 for a workspace nobody has
   * registered, a 500 from a runner that is down, a repo with nothing committed
   * to it — and all three mean the shelf is not drawn. That is not laziness
   * about error handling: there is no action any of them would prompt that the
   * wiki's own banner, three lines up the sidebar, is not already prompting.
   */
  const loadResearch = useCallback(async () => {
    try {
      const res = await authFetch(
        `/api/wiki/tree?workspace=${encodeURIComponent(RESEARCH_WORKSPACE)}`,
      );
      if (!res.ok) return setResearch(null);
      const body = (await res.json()) as { entries?: WikiEntry[] };
      const nodes = buildTree(body.entries ?? []);
      setResearch(nodes.length ? nodes : null);
    } catch (e) {
      // A rejected password still has to lock, or this would be the one request
      // that quietly swallows it and left the console looking signed in.
      if (e instanceof Unauthorized) return lock(e.message);
      setResearch(null);
    }
  }, [authFetch, lock]);

  /**
   * Read one file out of a workspace. The wiki unless told otherwise.
   *
   * The routes do their own fetching — which document is open is the address,
   * so the component at that address is the thing that should be asking for it.
   * What stays here is the part every caller shares: the token, and turning a
   * rejection into a re-prompt.
   */
  const readFile = useCallback(
    async (path: string, workspace = wikiSpace) => {
      const res = await authFetch(
        `/api/wiki/file?workspace=${encodeURIComponent(workspace)}` +
          `&path=${encodeURIComponent(path)}`,
      );
      const body = (await res.json()) as { text?: string; error?: string };
      if (!res.ok) throw new Error(body.error ?? 'could not read that file');
      return body.text ?? '';
    },
    [authFetch, wikiSpace],
  );

  /** A path as address segments, each encoded, slashes kept. */
  const segmentsOf = (path: string) =>
    path.split('/').filter(Boolean).map(encodeURIComponent).join('/');

  // There used to be a `carriedChat` here, hanging the open conversation off
  // every document address so that moving between documents did not close it.
  // Nothing carries it now, because there is nothing to carry: the rail lives
  // in storage and survives navigation on its own. What that function was
  // really working around is that a fact about the chrome had been written
  // into the address, so every `router.push` in the app had to remember to put
  // it back.

  /**
   * Open a document. A push, not a fetch.
   *
   * `workspace` is for a document named in a reply, which comes out of the repo
   * the conversation is working in rather than out of the wiki. It rides in the
   * query string because it is the exception: a path with no workspace on it is
   * the wiki, which is what every link from the tree is. It is also the only
   * thing left in a query string anywhere here, and it is about the document.
   *
   * On a phone the tree is a drawer over the document it just opened, so picking
   * a file has to get out of its own way — the same thing picking a conversation
   * does.
   */
  const openPath = useCallback(
    (path: string, workspace?: string) => {
      closeMenu();
      const q = workspace ? `?workspace=${encodeURIComponent(workspace)}` : '';
      router.push(`/wiki/${segmentsOf(path)}${q}`);
    },
    [closeMenu, router],
  );

  /**
   * The same, into the research repo.
   *
   * Its own address rather than `?workspace=research` on a wiki one, which the
   * file route would have served without a line of new code. The query string is
   * for the exception — one document, named in a reply, out of whatever repo
   * that conversation happens to be working in. Research is not an exception; it
   * is a place you go, with a shelf in the sidebar and pages you will want to
   * link to and come back to. An address that describes itself as a variation on
   * the wiki would be saying the opposite of what this repo is for.
   */
  const openResearch = useCallback(
    (path: string) => {
      closeMenu();
      router.push(`/research/${segmentsOf(path)}`);
    },
    [closeMenu, router],
  );

  /**
   * Every project head in the database, for the board.
   *
   * One request per project rather than one for all of them — eleven reads over
   * loopback, which is cheap enough that adding a runner endpoint to batch them
   * would mean a new route, a version bump and a restart to save nothing anyone
   * can perceive. If the database ever holds a hundred projects, that is the
   * moment to move this behind one call.
   *
   * It lives up here rather than in the board so that leaving the front door and
   * coming back does not redraw a page of skeletons over data this browser
   * already has.
   */
  const loadProjects = useCallback(
    async (nodes: TreeNode[]) => {
      const dirs: TreeNode[] = [];
      const walk = (list: TreeNode[]) => {
        for (const node of list) {
          if (isProject(node)) dirs.push(node);
          else if (node.type === 'dir') walk(node.children);
        }
      };
      walk(nodes);

      try {
        const entries = await Promise.all(
          dirs.map(async node => {
            const head = childNamed(node, 'project.md');
            const slug = node.path.slice(node.path.lastIndexOf('/') + 1);
            return {
              path: node.path,
              slug,
              project: parseProject(head ? await readFile(head.path) : '', node.name),
              synced: childNamed(node, 'state.md')?.last_commit ?? null,
            };
          }),
        );
        // Split rather than filtered, because the two halves are wanted in
        // different places: the board and everything derived from it — the
        // calendar, the unscheduled list — take the real projects and never have
        // to know a fixture exists, and the sidebar takes the paths so it can
        // leave those rows out of a tree that has no frontmatter to read. One
        // walk, two answers, and no call site filtering the same thing again.
        setProjects(entries.filter(e => !e.project.fixture));
        setFixtures(new Set(entries.filter(e => e.project.fixture).map(e => e.path)));
        setProjectsError(null);
      } catch (e) {
        if (e instanceof Unauthorized) return lock(e.message);
        // Said rather than swallowed, for the reason the tree says its failures:
        // a board that is silently empty is indistinguishable from a database
        // with nothing in it.
        setProjectsError((e as Error).message);
      }
    },
    [readFile, lock],
  );

  /**
   * The wiki, and the heads of the projects in it. One call, because the second
   * is read out of what the first returned.
   *
   * Both, on every route, rather than the board asking for the heads when it
   * mounts. That worked while the board was the only thing that read a project's
   * frontmatter — and the sidebar reads it now, to leave fixtures out of the
   * shelf, and the sidebar is on every page. On a wiki route nothing had ever
   * asked, so a fixture sat there like any other project.
   */
  const loadWiki = useCallback(async () => {
    const nodes = await loadTree();
    if (nodes) await loadProjects(nodes);
  }, [loadTree, loadProjects]);

  /*
   * The research shelf, fetched only once the registry has admitted it exists.
   *
   * Speculating was the first version and it was wrong in a way worth writing
   * down: the runner answers an unrecognised workspace name with a 400, so a
   * console that asked before the row existed logged a failed request on every
   * page it drew — invisible in the app, and enough to fail `pnpm shoot`, which
   * treats a dirty console as a defect. It was also a request that could never
   * succeed, sent once per load, forever.
   *
   * Deliberately not folded into `loadWiki`. That callback is a dependency of
   * the boot effect, which is what sets `workspaces` — making it depend on the
   * list it produces would close a loop. Keyed on `wikiVersion` as well as the
   * registry, which is the same "a turn has edited files" signal the wiki
   * refreshes on, and the case that matters here: a sweep writing into the
   * document you are looking at is the ordinary event in this repo, not an edge.
   */
  const hasResearch = workspaces.some(w => w.name === RESEARCH_WORKSPACE);
  useEffect(() => {
    if (!token || !hasResearch) return setResearch(null);
    void loadResearch();
  }, [token, hasResearch, wikiVersion, loadResearch]);

  /**
   * Re-read all of it after a turn that edited a file.
   *
   * Not called per edit — a turn can touch several files in a row, and a refetch
   * per tool call would hammer the wiki route for no reason the eye could follow.
   * The caller waits for the turn to end for good and calls this once.
   */
  const refreshWiki = useCallback(() => {
    setWikiVersion(v => v + 1);
    void loadWiki();
  }, [loadWiki]);

  /** Open or close one directory. A new Set every time, so React sees the change. */
  const toggleDir = useCallback((path: string) => {
    setExpanded(open => {
      const next = new Set(open);
      if (!next.delete(path)) next.add(path);
      return next;
    });
  }, []);

  /**
   * Re-read the session list.
   *
   * Cheap and worth doing often: it is what puts a brand-new conversation in the
   * sidebar under the title of its own first message, and what moves one back to
   * the top after you reply in it. A failure here is silent — a stale list is a
   * far smaller problem than an error banner over a working chat.
   */
  const refreshSessions = useCallback(async () => {
    try {
      const res = await authFetch('/api/sessions');
      if (!res.ok) return;
      const body = (await res.json()) as { sessions: SessionSummary[] };
      // A runner too old to send titles sends the raw store instead — every
      // session it ever made, including the empty shells, with nothing to label
      // them by. Rows with no title are not worth showing, so an old runner
      // degrades to an empty list rather than to a wall of blanks.
      setSessions(body.sessions.filter(s => s.title));
    } catch {
      /* see above */
    }
  }, [authFetch]);

  /**
   * Watch a turn until it ends, reconnecting for as long as it is still running.
   *
   * This is the part that makes a reload or an app switch survivable. The turn
   * belongs to the runner, so losing the connection is not an error to report —
   * it is a thing to recover from, by asking again for everything from the last
   * event sequence we actually saw.
   */
  const follow = useCallback(
    async (key: string, sessionId: string, turnId: string, from: number) => {
      const it = liveFor(key);
      if (it.watching?.turnId === turnId) return;
      detach(key);

      const mine = new AbortController();
      it.following = mine;
      it.watching = { turnId, cursor: from };
      patchThread(key, t => ({ ...t, busy: true }));

      // Set the moment any tool call in this turn names a file, and read only
      // once the turn is over for good — see the `end` frame below. A flag
      // rather than a counter: which file, and how many times, don't change
      // what happens next.
      let edited = false;

      /** One connection. Returns true when the turn is over for good. */
      const readOnce = async (signal: AbortSignal, onFrame: () => void) => {
        const cursor = it.watching?.cursor ?? from;
        const res = await authFetch(
          `/api/sessions/${sessionId}/turns/${turnId}/events?cursor=${cursor}`,
          { signal },
        );
        if (res.status === 404) throw new TurnGone();
        if (!res.ok || !res.body) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error ?? `could not attach to the turn (${res.status})`);
        }
        patchThread(key, t => ({ ...t, reconnecting: false }));

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        for (;;) {
          const { done, value } = await reader.read();
          if (done) return false; // ended without saying so: reconnect
          buffer += decoder.decode(value, { stream: true });

          // Frames are separated by a blank line; a partial frame stays in the
          // buffer. Nothing advances the cursor until a whole frame has arrived,
          // so a connection that dies mid-frame simply gets it again.
          const frames = buffer.split('\n\n');
          buffer = frames.pop() ?? '';

          for (const raw of frames) {
            if (!raw.trim()) continue;
            onFrame(); // keepalive comments count: the connection is alive
            const frame = parseFrame(raw);
            if (!frame) continue;
            if (frame.seq !== null && it.watching) {
              it.watching.cursor = frame.seq + 1;
            }

            if (frame.event === 'delta') {
              patch(key, t => ({
                ...t,
                text: t.text + String(frame.data.text ?? ''),
                writing: true,
              }));
            } else if (frame.event === 'tool') {
              // Back to work, so the marker lifts off the line. A tool is the
              // only signal in the stream that says prose has stopped for now
              // rather than stopped for good.
              if (typeof frame.data.path === 'string') edited = true;
              patch(key, t => ({
                ...t,
                tools: [...(t.tools ?? []), String(frame.data.name ?? '')],
                writing: false,
              }));
            } else if (frame.event === 'done') {
              patch(key, t => ({
                ...t,
                // Deltas already carry the full text; fall back to the final
                // result only if nothing streamed.
                text: t.text || String(frame.data.reply ?? ''),
                meta: {
                  duration_ms: frame.data.duration_ms as number,
                  cost_usd: frame.data.cost_usd as number,
                },
              }));
            } else if (frame.event === 'error') {
              const message = String(frame.data.message ?? 'turn failed');
              patch(key, t => ({
                ...t,
                failed: true,
                text: t.text ? `${t.text}\n\n${message}` : message,
              }));
              // Whatever was queued was written for a conversation that was
              // going somewhere else. Hold it rather than feeding the next
              // message into whatever just broke.
              hold(key);
            } else if (frame.event === 'end') {
              if (edited) refreshWiki();
              return true;
            }
          }
        }
      };

      try {
        for (let failures = 0; !mine.signal.aborted; ) {
          let alive = false;
          const conn = new AbortController();
          it.link = conn;
          const relay = () => conn.abort();
          mine.signal.addEventListener('abort', relay);
          try {
            if (await readOnce(conn.signal, () => (alive = true))) return;
          } catch (e) {
            if (e instanceof Unauthorized) return lock(e.message);
            if (e instanceof TurnGone) {
              // The runner no longer has this turn — almost always because it
              // restarted, which killed the turn with it. Go back to the
              // transcript rather than sitting here retrying a dead id.
              hold(key);
              setResync(n => n + 1);
              return;
            }
            if (mine.signal.aborted) return;
          } finally {
            mine.signal.removeEventListener('abort', relay);
          }

          failures = alive ? 0 : failures + 1;
          if (failures > MAX_RETRIES) {
            setError('Lost the connection to this turn. Reload to pick it back up.');
            // We no longer know whether the turn is running, so we certainly
            // cannot know when to send the next message.
            hold(key);
            return;
          }
          patchThread(key, t => ({ ...t, reconnecting: true }));
          await sleepUntilAwake(Math.min(500 * 2 ** failures, RETRY_CEILING_MS));
        }
      } finally {
        // Only tidy up if we are still the current follower — a newer turn may
        // have taken over while this loop was unwinding.
        if (it.following === mine) {
          it.following = null;
          it.link = null;
          it.watching = null;
          // Nobody was looking, so the tab has to say so — see `Thread.unread`.
          const away = frontNow.current !== key;
          patchThread(key, t => ({
            ...t,
            busy: false,
            reconnecting: false,
            unread: away || t.unread,
          }));
          // The turn is over, which is precisely the event the queue waits for.
          // After the cleanup, so the next turn starts against a clean slate.
          turnEnded.current(key);
        }
      }
    },
    [authFetch, detach, hold, lock, patch, patchThread, liveFor, refreshWiki],
  );

  /**
   * Load a session's conversation and make it the one on screen.
   *
   * Shared by the reload path and by clicking a row in the sidebar, because they
   * are the same operation — the browser has no privileged knowledge of the
   * session it happened to have open last, so restoring it is just opening it.
   *
   * A session the runner does not have (forgotten from another device, gone with
   * a restart, workspace dropped from the registry) returns false rather than
   * throwing: there is nothing the person can do about it, and the recovery —
   * fall back to the list — is the same in every case.
   *
   * A request that merely *failed* is not that, and the difference is the whole
   * point of the split below. Treating the two the same meant one 500 from a dev
   * server mid-rebuild, or one bad moment of phone signal, was read as "this
   * conversation no longer exists", and the page settled on the empty state with
   * a composer offering to start something new while the turn it should have
   * been watching carried on without it. Now that the address is what remembers,
   * a reload recovers from that — but only because this still tells the two
   * apart and throws rather than reporting the conversation gone.
   */
  const resume = useCallback(
    async (
      sessionId: string,
      available: Workspace[],
    ): Promise<{ thread: Thread; live: LiveTurn | null } | null> => {
      const res = await authFetch(`/api/sessions/${sessionId}/transcript`);
      // 404 is the runner saying it has no such session. That is the only answer
      // that means the address is wrong rather than the moment being wrong.
      if (res.status === 404) return null;
      // Anything else failed. Keep the bookmark, say so, and let the caller
      // decide — a reload will pick the conversation straight back up.
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `could not open that conversation (${res.status})`);
      }
      const transcript: {
        session_id: string;
        workspace: string;
        workspaces?: string[];
        model?: string;
        turns: Turn[];
        live: LiveTurn | null;
      } = await res.json();

      // What it was opened with, which for a conversation started before the
      // choice was a list is the one workspace it ran in. A conversation opened
      // with nothing chosen resolves to nothing, and that is not a failure —
      // only a *named* workspace this browser cannot see is.
      const opened =
        transcript.workspaces ??
        (transcript.workspace ? [transcript.workspace] : []);
      const picked = opened
        .map(name => available.find(w => w.name === name))
        .filter(Boolean) as Workspace[];
      if (picked.length !== opened.length) return null;

      // A name for it before there is anything to call it, so the queue and the
      // follow loop below have somewhere to be put.
      const key = nameThread();
      const it = liveFor(key);
      it.sessionId = transcript.session_id;

      // The queue that was stashed for *this* session, if there is one.
      //
      // Read rather than cleared: reading is repeatable and clearing is not,
      // and there are two passes through here whenever the caller retries,
      // since an attempt can get this far and still throw on the request after
      // it. Nothing clears what it is about to restore.
      const carried = stashedQueue(sessionId);

      // A turn is mid-flight — either it outlived this browser being away, or it
      // has been running since we last looked at this session. Put back the
      // message that started it and an empty reply for its output to land in,
      // then replay from the beginning: the server truncated the history above
      // at exactly this point, so nothing is shown twice.
      const turns = transcript.live
        ? [
            ...transcript.turns,
            { role: 'you' as const, text: transcript.live.prompt },
            { role: 'claude' as const, text: '', tools: [] },
          ]
        : transcript.turns;

      // Put back a queue this browser was holding for this session when it went
      // away. Whether it goes out by itself turns on whether the thing it was
      // waiting for is still ahead of it: a turn still running will end, and the
      // queue will go then, exactly as it would have. A turn that has already
      // ended took that moment with it while nobody was watching, so the queue
      // waits for a person instead of firing off the moment the page loads.
      if (carried.items.length) {
        // Ids are handed out from zero for each thread, so the counter has to
        // clear the restored ones or a new message would collide with one
        // already on screen and delete the wrong row.
        it.nextQueued = Math.max(...carried.items.map(item => item.id + 1));
        it.queue = { items: carried.items, held: carried.held || !transcript.live };
      }

      const thread: Thread = {
        key,
        session: {
          session_id: transcript.session_id,
          workspace: transcript.workspace,
          workspaces: opened,
          model: transcript.model,
          created_at: '',
          turns: transcript.turns.length,
          started: true,
        },
        chosen: picked,
        // The conversation's own choice, not this browser's last one. Opening an
        // old thread has to show what it will actually answer with — a picker
        // carrying over from the chat you just left would be a lie about the one
        // you just opened, and an expensive one in the wrong direction.
        model: isModel(transcript.model) ? transcript.model : DEFAULT_MODEL,
        turns,
        restoredCount: transcript.turns.length,
        draft: '',
        queue: it.queue,
        busy: false,
        reconnecting: false,
        modelError: null,
        pulse: 0,
        unread: false,
      };
      return { thread, live: transcript.live };
    },
    [authFetch, liveFor],
  );

  // A backgrounded tab's fetch can be left hanging rather than failing, so
  // coming back to the foreground forces a fresh connection instead of waiting
  // on a stream that may never produce another byte. Reconnecting is cheap: it
  // resumes from the cursor, and a healthy stream loses nothing by being redialled.
  useEffect(() => {
    // Every thread that is watching something, not just the one on screen — a
    // phone coming out of a pocket has been away from all of them equally.
    const refresh = () => {
      if (document.hidden) return;
      for (const it of live.current.values()) if (it.watching) it.link?.abort();
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('online', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('online', refresh);
    };
  }, []);

  /*
   * Keep the shell on the part of the screen a software keyboard has left.
   *
   * Safari answers a keyboard by leaving the layout viewport at full height and
   * sliding a shorter *visual* viewport down over it, so `100dvh` still measures
   * the whole phone and the top of the shell — the header, the way out of a
   * conversation — ends up above the glass. The fix is to stop asking the layout
   * viewport and ask the visual one: its height, at its offset.
   *
   * Chrome resizes the layout viewport instead, which is what the viewport meta
   * in layout.tsx asks for, and there the two viewports agree and this does
   * nothing. Which is the arrangement to keep: one browser needs the JS, the
   * other never sees it run.
   */
  useEffect(() => {
    const view = window.visualViewport;
    if (!view) return;
    const root = document.documentElement;
    const clear = () => {
      root.style.removeProperty('--viewport-top');
      root.style.removeProperty('--viewport-height');
    };

    const track = () => {
      // A pinch zoom shrinks the visual viewport too, and relaying out the app
      // under someone's fingers is not what they were asking for by zooming.
      const covered = view.scale > 1.01 ? 0 : window.innerHeight - view.height;
      // A keyboard takes a third of a phone. Nothing else that moves this number
      // — an address bar collapsing, a rounded fraction of a pixel — is close to
      // 80, and a threshold means the shell isn't re-pinned on every scroll.
      if (covered < 80) return clear();
      root.style.setProperty('--viewport-top', `${view.offsetTop}px`);
      root.style.setProperty('--viewport-height', `${view.height}px`);
    };

    track();
    // Both, because the keyboard changes the size and focusing a field near the
    // bottom then changes the offset, and only the second of those is a scroll.
    view.addEventListener('resize', track);
    view.addEventListener('scroll', track);
    return () => {
      view.removeEventListener('resize', track);
      view.removeEventListener('scroll', track);
      clear();
    };
  }, []);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    // Everything this browser needs to be looking at the right thing: who it is,
    // which workspaces exist, and which conversation it was in. One unit,
    // because a step that fails leaves every step after it undone anyway.
    const load = async () => {
      // First, and not in parallel with the rest. Identity decides which runner
      // answers everything below it, so a workspace list that arrived before we
      // knew whose it was would be a list we could not caption — and the wiki
      // would have no repo to read.
      const who = await authFetch('/api/me');
      const whoBody = await who.json().catch(() => ({}));
      if (!who.ok) throw new Error(whoBody.error ?? `could not identify you (${who.status})`);
      if (cancelled) return;
      setMe(whoBody as Me);

      const res = await authFetch('/api/workspaces');
      // Not `res.json()` alone: a failing route can answer in HTML, and parsing
      // first turns "the server is rebuilding" into "Unexpected token '<'".
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error ?? `could not load workspaces (${res.status})`);
      if (cancelled) return;

      setWorkspaces(body.workspaces as Workspace[]);
      void refreshSessions();
      // The board reads the tree, and the board is `/`. Whichever route this
      // browser actually landed on, it is one click from something that needs
      // this, so there is no longer a version of "not asked for yet" worth
      // keeping — see `tree` above.
      void loadWiki();
    };

    // A request that failed is worth asking again, and this is the moment where
    // giving up quietly costs the most: a turn may be running right now, and a
    // browser sitting on the empty state instead of the conversation is not
    // watching it, cannot stop it, and will not queue behind it. A dev server
    // finishing a rebuild, or a phone with one bar, should not be able to do
    // that — so this waits it out the way the follow loop does, and only says
    // something once waiting has stopped being the answer.
    (async () => {
      for (let attempt = 0; !cancelled; attempt++) {
        try {
          return await load();
        } catch (e) {
          if (cancelled) return;
          if (e instanceof Unauthorized) return lock((e as Error).message);
          if (attempt >= 3) return setError((e as Error).message);
          await sleepUntilAwake(500 * 2 ** attempt);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, resync, authFetch, lock, refreshSessions, loadWiki]);

  /**
   * Read the header's state off a scroller — see `topbarFloating`.
   *
   * Taken from the element rather than tracked as a delta, so it is correct
   * however the scroll happened: a wheel, a jump to the bottom, or a pane that
   * mounted already scrolled.
   *
   * THE PREVIOUS OBJECT IS RETURNED UNCHANGED when neither field has moved, and
   * that is the whole performance of scrolling in this app. This fires on every
   * scroll event — dozens a second — and it used to hand `setScrolled` a fresh
   * object literal each time. React compares with `Object.is`, so a new object
   * holding identical values is still a change, and every one of them
   * re-rendered this component: the sidebar, the board, the wiki tree, every
   * chat window and every turn in them, to answer a question whose answer was
   * `true` both times. Returning `prev` lets React bail out before any of that.
   * Measured on a long document with the main thread throttled 4x, scrolled
   * end to end: 7.3fps to 23.3 — see `pnpm perf`. On a dev build, so the real
   * figure is better; the ratio is the part that transfers.
   */
  const syncTopbar = useCallback(
    (el: HTMLElement | null) => {
      if (!el) return;
      const on = el.scrollTop > 8;
      setScrolled(prev => (prev.path === pathname && prev.on === on ? prev : { path: pathname, on }));
    },
    [pathname],
  );

  const topbarFloating = scrolled.path === pathname && scrolled.on;

  const onStreamScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => syncTopbar(e.currentTarget),
    [syncTopbar],
  );

  // Following a turn to the bottom of its own window used to live here, when
  // there was one scroller to follow. It is `Conversation`'s job now: each
  // window has its own scroller and its own turns, and a reply arriving in one
  // must not move another.

  // Escape closes the drawer. Only bound while it is open, so it can never eat
  // an Escape meant for anything else.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen, closeMenu]);

  // The model popover used to be run from here: its open flag, the two refs it
  // needs to tell "inside the menu" from "inside the trigger", the outside-click
  // and Escape listeners, the focus handling and the arrow keys. All of it has
  // gone to `useModelMenu` in ./chats/conversation, because with several windows
  // expanded there are several triggers and several menus, and one flag between
  // them would open the wrong one. It is a fact about a composer, not about the
  // app.

  /**
   * Start a chat in a workspace — locally. No session exists yet.
   *
   * It used to create one here, which meant browsing the picker minted a session
   * per click; a third of the store was empty shells nobody had said anything
   * to. A conversation should start existing when there is a conversation, so
   * the session is created by the first message instead. See `send`.
   *
   * This does not navigate, and the split is the point: `/chats/new` is the
   * address of a conversation that does not exist yet, so the page at that
   * address calls this when it arrives rather than this pushing the address it
   * is already on.
   */
  const beginDraft = useCallback(
    (picked: Workspace[]) => {
      setError(null);
      setMenuOpen(false);
      setArming(null);

      // A chat that was started and never typed into is not worth a second of.
      // Pressing New chat twice should not leave an empty conversation behind
      // to be closed by hand — but only an untouched one is reused, and only
      // for the same projects, since a draft with cards already ticked is a
      // decision someone has made.
      const spare = threadsNow.current.find(
        t =>
          !t.session &&
          !t.turns.length &&
          !t.draft.trim() &&
          t.chosen.length === picked.length &&
          t.chosen.every(w => picked.some(p => p.name === w.name)),
      );
      if (spare) {
        setFront(spare.key);
        focusThread(spare.key);
        return spare.key;
      }

      const key = nameThread();
      liveFor(key);
      const draft: Thread = {
        key,
        session: null,
        chosen: picked,
        // Back to the default rather than inheriting whatever the last chat
        // used. Sticky would be defensible for the cheap end and is not for
        // the dear one: a thread that needed Opus once would silently make
        // every new conversation after it cost the same.
        model: DEFAULT_MODEL,
        turns: [],
        restoredCount: 0,
        draft: '',
        queue: NOTHING_QUEUED,
        busy: false,
        reconnecting: false,
        modelError: null,
        pulse: 0,
        unread: false,
      };
      // Added to the end, so the list reads in the order things were opened.
      setThreads(all => [...all, draft]);
      // And mirrored now rather than on the next commit, because `newChat`
      // raises this thread in the same handler and raising it writes the rail
      // down — from `threadsNow`, which without this line does not yet have
      // the draft it is being asked to record.
      threadsNow.current = [...threadsNow.current, draft];
      setFront(key);
      focusThread(key);
      // Handed back because the thread has no session id yet and state is a
      // commit away: a caller that starts a chat and sends into it in one
      // gesture — see `reanalyze` — has no other way to name what it just made.
      return key;
    },
    [liveFor, focusThread, setFront],
  );

  /**
   * Start a conversation, without leaving the page.
   *
   * The projects are a starting point and not a prerequisite: called with none,
   * this opens a chat with the cards unticked and the composer live, which is
   * what "start a conversation somewhere else" on the board now means. Called
   * with some — a card on the board, a reanalysis — it opens the same chat with
   * those already ticked.
   *
   * This used to be a navigation — `/chats/new?workspace=x`, a route that drew
   * a draft. The draft is now the right-hand pane's own state, so the address
   * only records that there is one; the middle column stays on whatever you
   * were reading, which is the thing the new shape is for. You start a chat
   * *about* what is in front of you and it does not take the page away.
   */
  const newChat = useCallback(
    (picked: Workspace[] = []) => {
      closeMenu();
      const key = beginDraft(picked);
      setArchiveOpen(false);
      // Which also writes the rail down — the draft and its projects included.
      raiseThread(key);
      return key;
    },
    [closeMenu, beginDraft, raiseThread],
  );

  /**
   * Tick or untick one project on a chat that has not been sent to yet.
   *
   * Only before the first message, and the composer's own guard is not what
   * enforces that — `send` fixes the working directory when it creates the
   * session, so a card that could still be toggled afterwards would be showing
   * a choice that no longer decides anything. The cards come off screen instead.
   *
   * Order is the order they were ticked, because the first one is the working
   * directory. Unticking and re-ticking moves a project to the end, which is
   * the honest reading of "picked last".
   */
  const toggleProject = useCallback(
    (key: string, ws: Workspace) => {
      const thread = threadsNow.current.find(t => t.key === key);
      if (!thread) return;
      const next = thread.chosen.some(w => w.name === ws.name)
        ? thread.chosen.filter(w => w.name !== ws.name)
        : [...thread.chosen, ws];
      patchThread(key, t => ({ ...t, chosen: next }));
      // The stash carries a draft's choice, so a reload keeps the cards you
      // ticked. Mirrored by hand first, because `patchThread` only queues the
      // change and `stashRail` reads the ref — without this the rail would be
      // written from the ticks as they were before the press.
      //
      // Unconditional, unlike the `router.replace` this replaced: that had to
      // be held back to the draft in front, because the address could only name
      // one conversation and a background window ticking a card would have
      // seized the pane. The rail has room for every thread at once, so there
      // is nothing to fight over and nothing to guard against.
      threadsNow.current = threadsNow.current.map(t =>
        t.key === key ? { ...t, chosen: next } : t,
      );
      stashRail();
      focusThread(key);
    },
    [patchThread, focusThread, stashRail],
  );

  /**
   * Add a repo to the list you can pick from — clone a git url, or adopt a
   * directory already on the box.
   *
   * The list is patched in place rather than refetched. A clone can take a
   * while and the answer already carries the row the runner wrote, so going
   * back for the whole list would only be a second wait for something we have.
   *
   * Errors are thrown rather than parked in `error`: the form that called this
   * is the right place to show "that url has no repo behind it", where the url
   * still is, rather than the banner over the whole app.
   */
  const addWorkspace = useCallback(
    async (spec: {
      url?: string;
      path?: string;
      /** Make an empty repo here rather than cloning or adopting one. */
      create?: boolean;
      name?: string;
      project?: string;
      kind?: RepoKind;
      description?: string;
    }): Promise<Workspace> => {
      const res = await authFetch('/api/workspaces', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(spec),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error ?? `could not add that repo (${res.status})`);
      const added = body.workspace as Workspace;
      setWorkspaces(all => [...all.filter(w => w.name !== added.name), added]);
      return added;
    },
    [authFetch],
  );

  /**
   * Relabel a repo — what it is, and therefore what can be done to it.
   *
   * Replaced in place rather than appended, unlike `addWorkspace`: this row is
   * already in the list and in a position the registry decided, and moving it
   * to the end would reshuffle the picker every time somebody corrected a kind.
   */
  const setKind = useCallback(
    async (name: string, kind: RepoKind): Promise<Workspace> => {
      const res = await authFetch(`/api/workspaces/${encodeURIComponent(name)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error ?? `could not change that (${res.status})`);
      const updated = body.workspace as Workspace;
      setWorkspaces(all => all.map(w => (w.name === updated.name ? updated : w)));
      return updated;
    },
    [authFetch],
  );

  /**
   * Change what this person has told us about themselves.
   *
   * The server answers with the whole public view rather than an acknowledgement,
   * so what lands in state is what the registry now says — not what we asked it
   * to say. Those are the same thing until a write is refused, and that is
   * exactly when the difference matters.
   */
  const saveMe = useCallback(
    async (patch: {
      technical?: boolean;
      onboarded?: boolean;
      wiki_workspace?: string;
    }): Promise<Me> => {
      const res = await authFetch('/api/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error ?? `could not save that (${res.status})`);
      const next = body as Me;
      setMe(next);
      return next;
    },
    [authFetch],
  );

  /**
   * Bring a saved conversation into the right-hand pane, wherever you are.
   *
   * A plain call now. It used to write `?chat=<id>` and let an effect in
   * `shell.tsx` notice the address had changed and go and fetch it — a round
   * trip through the router to reach a function in the same file, which is
   * what having the pane live in the address cost. Declared late because
   * `openSession` is defined below it; the `useCallback` is what lets the
   * archive hold on to one identity across every render of the list.
   */
  const showChat = useCallback(
    (id: string) => {
      setArchiveOpen(false);
      void openSessionRef.current(id);
    },
    [],
  );

  /**
   * Bring a thread that is already open to the front.
   *
   * Not the same as `showChat`, which names a conversation that may not be on
   * the rail yet and will be fetched if it is not. This one is for something
   * already here — a draft included, which is why it takes a thread key rather
   * than a session id.
   */
  const showThread = useCallback(
    (key: string) => {
      if (!threadsNow.current.some(t => t.key === key)) return;
      // Which raises it, moves the pane, and writes the rail down.
      raiseThread(key);
    },
    [raiseThread],
  );

  /**
   * Close a conversation: stop watching it and take it out of the list.
   *
   * A turn running in it is not stopped — the runner owns it and carries on,
   * exactly as it does when you close the tab. What ends is this browser's
   * interest, which is why the queue goes too: a queue is what you were about
   * to say here, and closing the thread says you are not.
   *
   * The one in front hands over to its neighbour rather than to nothing, which
   * is what every other set of tabs does and the only behaviour that does not
   * punish closing the thing you are looking at.
   */
  const closeThread = useCallback(
    (key: string) => {
      const at = threadsNow.current.findIndex(t => t.key === key);
      if (at < 0) return;
      const remaining = threadsNow.current.filter(t => t.key !== key);
      dropThread(key);
      // Set synchronously, same as `threadsNow` mirrors on other paths — the
      // `lowerThread` and `stashRail` calls below read this ref for the rail
      // they write, and the effect that would otherwise catch it up has not run
      // yet on this tick.
      threadsNow.current = remaining;
      setThreads(all => all.filter(t => t.key !== key));
      // Its window goes with it, which also writes the rail down without this
      // thread on it — the one act that takes a conversation off the rail for
      // good, as against folding it, which only makes it a pill.
      lowerThread(key);
      if (frontNow.current !== key) return;

      const next = remaining[Math.min(at, remaining.length - 1)] ?? null;
      setFront(next?.key ?? null);
      // Again, because the pane moved after `lowerThread` had already written.
      stashRail();
    },
    [dropThread, lowerThread, setFront, stashRail],
  );

  /**
   * Change which model this conversation answers with.
   *
   * On screen immediately, whether or not there is anything to tell the runner
   * about yet: before the first message the choice rides along on the request
   * that creates the session, and after it the runner is told directly. Either
   * way it lands on the next turn, never the one already running.
   *
   * A failed PATCH puts the picker back rather than leaving it showing a model
   * the conversation is not going to use. That is the whole reason this is not
   * fire-and-forget — the one thing worse than not switching is believing you
   * did.
   *
   * Why it says so *at the composer* rather than in the page banner: the banner
   * is the first child of `<main>`, above the whole scrolling conversation, and
   * the picker is in the dock at the bottom of it. In a thread of any length
   * those are not on the same screen. The failure was being reported the whole
   * time and read as the picker silently refusing — which is the exact belief
   * this function exists to prevent, arrived at by a different route.
   */
  const chooseModel = useCallback(
    async (key: string, next: Model) => {
      const thread = threadsNow.current.find(t => t.key === key);
      if (!thread) return;
      const { session: current, model: previous } = thread;
      patchThread(key, t => ({ ...t, model: next, modelError: null }));
      if (!current) return;
      try {
        const res = await authFetch(`/api/sessions/${current.session_id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ model: next }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error ?? `could not switch model (${res.status})`);
        }
        patchThread(key, t => ({ ...t, session: t.session && { ...t.session, model: next } }));
      } catch (e) {
        if (e instanceof Unauthorized) {
          patchThread(key, t => ({ ...t, model: previous }));
          lock(e.message);
          return;
        }
        patchThread(key, t => ({ ...t, model: previous, modelError: (e as Error).message }));
      }
    },
    [patchThread, authFetch, lock],
  );

  /**
   * Load a conversation onto the rail, and say where on it to put it.
   *
   * Called by the archive, by the `/chats/[id]` forward, and — for every
   * conversation at once — by the restore in `shell.tsx`.
   */
  const openSession = useCallback(
    async (id: string, opts?: { focus?: boolean; unfold?: boolean; at?: number }) => {
      // `focus` is false for the other windows a reload is putting back —
      // they rejoin the row without fighting the one the rail named for the
      // front. See `raiseThreadBackground`.
      //
      // `unfold` is false only for a pill a reload is putting back down: a
      // thread that had been folded before the reload should come back folded,
      // not spring open just because reading it back in required asking the
      // runner for its transcript.
      //
      // The two are independent, and have to be: the conversation the pane was
      // showing when the tab closed may well have been folded down to a pill,
      // and a restore that could only offer "front and open" or "background and
      // folded" would have to lie about one of them.
      //
      // `at` is the rail position this one is being restored into — see
      // `insertByRank`. Absent for anything opened by hand, which belongs on
      // the end wherever the row happens to be up to.
      if (opts?.at !== undefined) railOrder.current.set(id, opts.at);
      const focus = opts?.focus ?? true;
      const unfold = opts?.unfold ?? true;
      const place = (key: string) => {
        if (unfold) return void (focus ? raiseThread(key) : raiseThreadBackground(key));
        // A pill. Nothing to raise, so the two things `raiseThread` would have
        // done on the way past have to be done here instead.
        if (focus) setFront(key);
        stashRail();
      };
      setError(null);
      setMenuOpen(false);
      setArming(null);
      // An address naming a conversation is a request to see it, so the window
      // comes up. `showChat` already did this and this did not, which was the
      // same intent arriving by two routes and only one of them working: a link
      // opened cold landed on a conversation with its window still down. It was
      // invisible while the chat was a column that was always there.
      setArchiveOpen(false);

      // Retried, with the same backoff the whole load used to sit inside.
      //
      // That loop used to wrap this, because opening a conversation was a step
      // of starting the app. It is a route now, asked for once when the address
      // arrives — so moving the retry out of the caller and into the operation
      // is what keeps the property the loop existed for: one 500 from a dev
      // server mid-rebuild, or one bad moment of phone signal, is a moment
      // rather than a verdict. `restorecheck` fails loudly if this goes.
      //
      // A 404 is not in this loop, deliberately. It is the one answer that means
      // the conversation is wrong rather than the moment — see `resume`.
      // Already open, so this is a switch rather than a load. Nothing is
      // fetched, nothing is torn down, and a turn running in it has been
      // running the whole time you were somewhere else.
      const already = threadsNow.current.find(t => t.session?.session_id === id);
      if (already) {
        place(already.key);
        return;
      }

      // Nor is a load of it already in flight. A restore asks for the whole
      // rail at once and the pane on top of that, so the same id can be asked
      // for twice within a tick — and the guard above cannot see the first
      // one, which will not be in `threadsNow` until its transcript lands.
      // Without this the reload comes back with the conversation on the rail
      // twice, each copy following the same turn.
      if (loading.current.has(id)) return;
      loading.current.add(id);

      // Which request this is, so that two rows clicked in quick succession
      // settle on the one clicked second rather than on whichever transcript
      // happened to come back last. Only `focus` calls take a number — a
      // background restore does not compete for the front, and counting it
      // here would let it bump the counter out from under a `focus` call that
      // started first and is still waiting on its own transcript, silencing
      // the raise that call is owed when it finally lands.
      const mine = focus ? ++opening.current : -1;

      try {
        for (let attempt = 0; ; attempt++) {
          try {
            const opened = await resume(id, workspaces);
            if (!opened) {
              // It is gone rather than broken. Drop it from the list so the row
              // that points at it doesn't sit there looking clickable.
              setSessions(all => all.filter(s => s.session_id !== id));
              setError('That conversation is no longer available.');
              return;
            }

            // Added, not swapped in. Whatever was already open stays open and
            // stays followed — this is the whole of what "several conversations
            // at once" means, and everything above had to stop assuming
            // otherwise before this line could be written.
            //
            // At its rail position rather than on the end, which matters only
            // during a restore and matters a lot there: eight conversations
            // asked for at once come back in whatever order their transcripts
            // do, and appending would rebuild the row in fetch order every
            // reload. Anything opened by hand has no rail position and lands
            // on the end, which is where it belongs.
            setThreads(all => insertByRank(all, opened.thread));
            // `threadsNow` mirrors `threads` through an effect that has not run
            // yet — `place`, called on the next line, writes the rail from this
            // thread's session id immediately, so it needs the mirror caught up
            // now rather than after the next commit.
            threadsNow.current = insertByRank(threadsNow.current, opened.thread);
            // Unfolded, in front, or neither — see `place`. Asking for a
            // conversation ordinarily means asking to read it, so a thread that
            // arrived as a folded pill used to be the archive appearing to do
            // nothing at all; a reload putting a pill back as a pill is the one
            // case that is supposed to look like that.
            if (!focus || opening.current === mine) place(opened.thread.key);
            // After the thread is in the list, so the frames it patches have
            // something to land in.
            if (opened.live) {
              void follow(opened.thread.key, id, opened.live.turn_id, 0);
            }
            return;
          } catch (e) {
            if (e instanceof Unauthorized) return lock(e.message);
            if (attempt >= 3) return setError((e as Error).message);
            await sleepUntilAwake(500 * 2 ** attempt);
          }
        }
      } finally {
        // However this ended — opened, gone, or given up on — it is no longer
        // in flight, and a later press must be able to ask for it again.
        loading.current.delete(id);
      }
    },
    [resume, workspaces, lock, follow, raiseThread, raiseThreadBackground, setFront, stashRail, insertByRank],
  );

  // Where `showChat` reaches it from, several hundred lines above.
  openSessionRef.current = openSession;

  /**
   * Forget a session. Two clicks, because it cannot be undone from here — though
   * it is less final than it sounds: the runner drops its mapping and Claude
   * Code's transcript stays on disk.
   */
  const forget = useCallback(
    async (id: string) => {
      setArming(null);
      try {
        const res = await authFetch(`/api/sessions/${id}`, { method: 'DELETE' });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setError(body.error ?? `could not forget that conversation (${res.status})`);
          return;
        }
        setSessions(all => all.filter(s => s.session_id !== id));
        // Forgetting a conversation leaves any thread holding it showing turns
        // that no longer belong to anything. Close those threads — and only
        // those. What you were reading in the middle column has nothing to do
        // with which chat you just deleted.
        for (const t of threadsNow.current) {
          if (t.session?.session_id === id) closeThread(t.key);
        }
      } catch (e) {
        if (e instanceof Unauthorized) lock(e.message);
        else setError((e as Error).message);
      }
    },
    [authFetch, closeThread, lock],
  );

  /**
   * Send a message: start a turn, then watch it.
   *
   * Two requests rather than one, deliberately. The turn is registered and
   * running before we ask to see any of it, so from here on this browser is only
   * ever a viewer of something the server owns.
   *
   * The text is passed in rather than read from the composer, because the caller
   * may be the queue rather than the person. Returns false if the message never
   * left — the session would not open, nothing was put on screen, and the text is
   * still the caller's to put back where it came from.
   */
  const send = useCallback(
    async (
      key: string,
      text: string,
      into?: { picked: Workspace[]; model: Model },
    ): Promise<boolean> => {
      // `into` is for a send that opens its own conversation in the same gesture
      // — the reanalysis button, which picks a project and sends in one press.
      // React has not re-rendered by then, so the thread's own `chosen` and
      // `model` are not readable yet. The caller passes what it just chose
      // rather than this reading state that is one tick stale.
      const held = threadsNow.current.find(t => t.key === key);
      const picked = into?.picked ?? held?.chosen ?? NO_WORKSPACES;
      const sendModel = into?.model ?? held?.model ?? DEFAULT_MODEL;
      if (!text) return false;

      const it = liveFor(key);
      setError(null);
      // Starting a turn is the answer to whatever paused the queue — otherwise a
      // hold set long ago, on messages since deleted, would silently strand the
      // next thing typed. A no-op when the queue is what started this turn.
      updateQueue(key, q => (q.held ? { ...q, held: false } : q));
      patchThread(key, t => ({ ...t, busy: true }));

      // Creating the session happens before the message is put on screen, so a
      // failure here has swallowed nothing: the caller still has the text.
      //
      // Read off the live record rather than off state, because a queue draining
      // into a conversation whose first message created it is exactly the case
      // where state is a commit behind — see `Live`.
      let sessionId = it.sessionId;
      if (!sessionId) {
        try {
          const res = await authFetch('/api/sessions', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              workspaces: picked.map(w => w.name),
              model: sendModel,
            }),
          });
          const body = await res.json();
          if (!res.ok) throw new Error(body.error ?? 'could not open a session');
          const opened = body as Session;
          sessionId = opened.session_id;
          it.sessionId = sessionId;
          // The one line in this file that is about where something is on
          // screen, and it is here because this is where it happens: a draft
          // lives in the New control and a conversation lives in the row, so
          // gaining a session moves it. Two elements, briefly, which is the
          // case a view transition is for — `dock.tsx` names both sides after
          // the thread so the browser can pair them.
          //
          // Only on a desktop. On a phone a sheet is a sheet wherever the
          // thread is rendered from, so nothing moves and there is nothing to
          // interpolate.
          const become = () => patchThread(key, t => ({ ...t, session: opened }));
          if (wide) morph(become);
          else become();
          threadsNow.current = threadsNow.current.map(t =>
            t.key === key ? { ...t, session: opened } : t,
          );
          // Anything queued behind this first message now has somewhere to be
          // written down — see `stashQueues`, which had nothing to key on until
          // this line ran.
          stashQueues();
          // And the rail stops calling this one a draft and starts naming it,
          // which is the whole of what used to be a `router.replace` off
          // `chat=new`. Unconditional, where that had to be held to the thread
          // in front: a conversation that finished opening while you were
          // reading another one is still a conversation, and the rail has a
          // place for it without touching which one the pane is showing.
          stashRail();
        } catch (e) {
          if (e instanceof Unauthorized) lock(e.message);
          else setError((e as Error).message);
          patchThread(key, t => ({ ...t, busy: false }));
          focusThread(key);
          return false;
        }
      }

      patchThread(key, t => ({
        ...t,
        turns: [...t.turns, { role: 'you', text }, { role: 'claude', text: '', tools: [] }],
      }));

      try {
        const res = await authFetch(`/api/sessions/${sessionId}/turns`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text }),
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body.error ?? `could not start the turn (${res.status})`);

        patchThread(key, t => ({
          ...t,
          session: t.session && { ...t.session, turns: t.session.turns + 1, started: true },
        }));
        // Before following, not after: the turn is what puts a new conversation
        // in the sidebar, and `follow` does not return until that turn has
        // finished. Refreshing only on the way out meant a chat you had just
        // started stayed missing from the list for as long as the answer took.
        void refreshSessions();
        await follow(key, sessionId, body.turn_id, 0);
      } catch (e) {
        if (e instanceof Unauthorized) {
          lock(e.message);
          return true;
        }
        const message = (e as Error).message;
        setError(message);
        patch(key, t => (t.text ? t : { ...t, failed: true, text: message }));
        patchThread(key, t => ({ ...t, busy: false }));
        hold(key);
      } finally {
        // Again on the way out, for what only the finished turn knows: when it
        // last spoke, and so where it sits in the list. Also the one refresh a
        // send that never left gets at all.
        void refreshSessions();
      }
      return true;
    },
    [
      authFetch,
      focusThread,
      follow,
      hold,
      liveFor,
      lock,
      patch,
      patchThread,
      refreshSessions,
      stashQueues,
      stashRail,
      updateQueue,
      wide,
    ],
  );

  /**
   * Where each project's reanalysis stands — not started, running, done or
   * errored — keyed by project name. Independent of any conversation, since a
   * reanalysis no longer opens one; see runner/runner.py, "Analysis jobs".
   */
  const [analysisJobs, setAnalysisJobs] = useState<Record<string, AnalysisStatus>>({});
  // One poll loop per project, so a page reload that finds a job already
  // running and a fresh button press never stack a second timer on the first.
  const pollTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  useEffect(() => {
    const timers = pollTimers.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  const fetchAnalysis = useCallback(
    async (project: string): Promise<AnalysisStatus | null> => {
      try {
        const res = await authFetch(`/api/analysis/${encodeURIComponent(project)}`);
        const body = await res.json();
        if (!res.ok) throw new Error(body.error ?? 'could not read analysis status');
        setAnalysisJobs(all => ({ ...all, [project]: body }));
        return body as AnalysisStatus;
      } catch (e) {
        if (e instanceof Unauthorized) lock(e.message);
        // A poll tick that fails to update a pill is a smaller loss than an
        // error banner for a check nobody asked to watch.
        return null;
      }
    },
    [authFetch, lock],
  );

  const pollAnalysis = useCallback(
    (project: string) => {
      if (pollTimers.current[project]) return;
      const tick = () => {
        void fetchAnalysis(project).then(status => {
          if (status?.status === 'running') {
            pollTimers.current[project] = setTimeout(tick, ANALYSIS_POLL_MS);
            return;
          }
          delete pollTimers.current[project];
          // The two files it writes just changed, so the tree this project
          // page reads its commit ages from is stale the moment the job ends.
          if (status?.status === 'done') refreshWiki();
        });
      };
      pollTimers.current[project] = setTimeout(tick, ANALYSIS_POLL_MS);
    },
    [fetchAnalysis, refreshWiki],
  );

  /**
   * Whatever this project's reanalysis is doing right now, fetched once and
   * followed if it turns out to be running. Called when a project page opens,
   * so a job started from another tab or before a reload still shows as
   * running instead of reading "never analysed" until the next press.
   */
  const checkAnalysis = useCallback(
    (project: string) => {
      void fetchAnalysis(project).then(status => {
        if (status?.status === 'running') pollAnalysis(project);
      });
    },
    [fetchAnalysis, pollAnalysis],
  );

  /**
   * Start a reanalysis of one project.
   *
   * Runs on the runner as a hidden session and turn, same as any other turn —
   * this only starts it and begins polling for status. No chat thread opens,
   * and nothing here waits for it to finish; the pill by the button does.
   */
  const reanalyze = useCallback(
    (node: TreeNode) => {
      // The directory segment, not `node.name` — that has been through `title()`
      // and comes out as "A Night at Ashworth Manor", which is a heading rather
      // than a path. The skill resolves `projects/<name>/`, so what it wants is
      // the kebab the directory is actually called.
      const projectName = node.path.slice(node.path.lastIndexOf('/') + 1);
      setError(null);
      setAnalysisJobs(all => ({
        ...all,
        [projectName]: { project: projectName, status: 'running' },
      }));
      void (async () => {
        try {
          const res = await authFetch(`/api/analysis/${encodeURIComponent(projectName)}`, {
            method: 'POST',
          });
          const body = await res.json();
          if (!res.ok) throw new Error(body.error ?? 'could not start the reanalysis');
          setAnalysisJobs(all => ({ ...all, [projectName]: body }));
          pollAnalysis(projectName);
        } catch (e) {
          if (e instanceof Unauthorized) {
            lock(e.message);
            return;
          }
          const message = (e as Error).message;
          setError(message);
          setAnalysisJobs(all => ({
            ...all,
            [projectName]: { project: projectName, status: 'error', error: message },
          }));
        }
      })();
    },
    [authFetch, lock, pollAnalysis],
  );

  /**
   * Send everything waiting, as one message.
   *
   * All of it and in order, rather than one message per turn. Messages typed
   * behind a running turn are nearly always about the same thing — a correction,
   * then the reason for it, then the bit that was forgotten — and sending them
   * separately gets the first one answered without the other two, which is how
   * you get an answer to a question nobody had finished asking. Together they
   * read as what they were: one person's next thing to say, in the order it was
   * written. Anything typed after this goes out has a turn of its own to wait
   * for, and queues behind it.
   *
   * A blank line between them and no other marker, because that is what the
   * pauses looked like from this side. Inventing a syntax to say "these were
   * three separate boxes" would be telling Claude about this app rather than
   * about the work.
   *
   * Called when a turn ends — see `turnEnded` — and when a held queue is
   * released, which are the only two moments the answer can change.
   */
  const drain = useCallback(
    (key: string) => {
      const { items, held } = liveFor(key).queue;
      if (held || !items.length) return;
      // By id rather than by emptying, so anything queued while this was being
      // assembled is still there afterwards, waiting for the turn it just started.
      const going = new Set(items.map(item => item.id));
      updateQueue(key, q => ({ ...q, items: q.items.filter(item => !going.has(item.id)) }));
      void send(key, items.map(item => item.text).join('\n\n')).then(sent => {
        if (sent) return;
        // It never left. Put it back at the front — where it still is, as far as
        // anyone watching this was concerned — and stop, rather than spinning.
        updateQueue(key, q => ({ items: [...items, ...q.items], held: true }));
      });
    },
    [liveFor, send, updateQueue],
  );

  // The follow loop is defined above `send` and reaches the drain through here.
  useEffect(() => {
    turnEnded.current = drain;
  }, [drain]);

  /**
   * What is typed into a conversation, and the flash when it leaves.
   *
   * Named rather than implied: these took no argument while there was one
   * composer and "the one in front" was the only thing they could have meant.
   * With several windows expanded that is no longer an answer — the front one
   * is where the keyboard is, not where every message goes — so every one of
   * these now says which conversation it is about.
   *
   * They keep the `useState` shape the composer has always called them with, a
   * value or an updater, so only the first argument is new.
   */
  const setDraft = useCallback(
    (key: string, next: string | ((d: string) => string)) => {
      patchThread(key, t => ({
        ...t,
        draft: typeof next === 'function' ? next(t.draft) : next,
      }));
    },
    [patchThread],
  );

  const setPulse = useCallback(
    (key: string, next: number | ((n: number) => number)) => {
      patchThread(key, t => ({
        ...t,
        pulse: typeof next === 'function' ? next(t.pulse) : next,
      }));
    },
    [patchThread],
  );

  /** Ask the runner to stop the turn. Its own `end` event is what closes it out. */
  async function stopTurn(key: string) {
    const it = live.current.get(key);
    const current = it?.watching;
    if (!it?.sessionId || !current) return;
    // Stopping is a decision about the whole conversation, not just this turn.
    // Whatever is queued stays on screen, and stays unsent, until it is released.
    hold(key);
    try {
      const res = await authFetch(
        `/api/sessions/${it.sessionId}/turns/${current.turnId}/cancel`,
        { method: 'POST' },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error ?? `could not stop the turn (${res.status})`);
      }
    } catch (e) {
      if (e instanceof Unauthorized) lock((e as Error).message);
      else setError((e as Error).message);
    }
  }

  // Whether the workspace is worth naming on every row. With one repo in the
  // list it is the same word all the way down; with several it is the only thing
  // telling two similarly-titled conversations apart.
  const manyWorkspaces = new Set(sessions.map(s => s.workspace)).size > 1;

  /**
   * Whether what is typed into a thread joins a queue instead of going out.
   *
   * Anything already waiting has to go first, so a queue that is not empty keeps
   * taking messages even once the turn that started it has ended — otherwise
   * Enter would jump the line.
   */
  const queueingIn = useCallback(
    (t: Thread) => t.busy || t.queue.items.length > 0,
    [],
  );

  /** Enter, and the button beside it: put what was typed wherever it goes next. */
  function submitDraft(key: string) {
    const thread = threadsNow.current.find(t => t.key === key);
    if (!thread) return;
    const text = thread.draft.trim();
    if (!text) return;
    setDraft(key, '');
    // The field acknowledges the words leaving it. Both ways out — straight to
    // the runner, or onto the queue — because from here they are the same
    // gesture: what was in the box is not in the box any more, and something
    // ought to say so before the answer starts arriving.
    setPulse(key, n => n + 1);
    // Sending by button used to hand focus back only once the turn ended, since
    // the composer was disabled until then. It isn't any more, so this is simply
    // where focus goes after the button is pressed — and it has to name the
    // window, because there may be three of them with a field each.
    focusThread(key);
    if (queueingIn(thread)) {
      const it = liveFor(key);
      const item = { id: it.nextQueued++, text };
      updateQueue(key, q => ({ ...q, items: [...q.items, item] }));
      return;
    }
    void send(key, text).then(sent => {
      // The session would not open. Nothing was put on screen, so put the words
      // back — unless something has been typed in the meantime, which is the one
      // case where restoring them would overwrite something.
      if (!sent) patchThread(key, t => ({ ...t, draft: t.draft || text }));
    });
  }

  /** Delete a queued message. Only possible while it is still queued, by design. */
  function unqueue(key: string, id: number) {
    updateQueue(key, q => ({ ...q, items: q.items.filter(item => item.id !== id) }));
  }

  /** Let a held queue go again — and, if nothing is running, start it moving. */
  function release(key: string) {
    const thread = threadsNow.current.find(t => t.key === key);
    updateQueue(key, q => ({ ...q, held: false }));
    if (!thread?.busy) drain(key);
  }

  function onKeyDown(key: string, e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitDraft(key);
    }
  }

  // What the narrow-screen bar calls the thing you are looking at. The title
  // comes from the session list rather than being tracked separately, so it is
  // the same string the sidebar row shows — and it appears as soon as the first
  // message gives the session a title.
  const headline = session
    ? (sessions.find(s => s.session_id === session.session_id)?.title ?? 'Conversation')
    : drafting
      ? 'New chat'
      : 'console';

  // One object with everything on it, deliberately. This was a single component
  // and every one of these was a local: a context per concern would be a shape
  // the app has not earned yet, and the cost of getting it wrong now is a
  // refactor of every route rather than of this line.
  const value = useMemo(
    () => ({
      token,
      me,
      ready,
      wikiSpace,
      workspaces,
      sessions,
      manyWorkspaces,
      chosen,
      drafting,
      toggleProject,
      addWorkspace,
      setKind,
      saveMe,
      session,
      turns,
      restoredCount,
      headline,
      busy,
      queueingIn,
      reconnecting,
      error,
      setError,
      queue,
      draft,
      setDraft,
      pulse,
      setPulse,
      model,
      modelError,
      chooseModel,
      lendInput,
      focusThread,
      tree,
      treeError,
      research,
      wikiVersion,
      projects,
      fixtures,
      projectsError,
      expanded,
      toggleDir,
      openPath,
      openResearch,
      readFile,
      authFetch,
      lock,
      beginDraft,
      newChat,
      showChat,
      // Every conversation this browser is holding open, so the pane can offer
      // them all rather than only the one it happens to be showing.
      threads,
      frontKey,
      showThread,
      closeThread,
      chatOpen,
      closeChat,
      openKeys,
      archiveOpen,
      raiseThread,
      lowerThread,
      raiseArchive,
      setArchiveOpen,
      wide,
      openSession,
      forget,
      arming,
      setArming,
      reanalyze,
      analysisJobs,
      checkAnalysis,
      submitDraft,
      unqueue,
      release,
      onKeyDown,
      stopTurn,
      menuOpen,
      setMenuOpen,
      closeMenu,
      menuButton,
      accountOpen,
      setAccountOpen,
      topbarFloating,
      onStreamScroll,
      dock,
      topbar,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      token, me, ready, wikiSpace, workspaces, sessions, manyWorkspaces, chosen, drafting, toggleProject,
      addWorkspace, setKind, saveMe, session, turns,
      restoredCount, headline, busy, queueingIn, reconnecting, error, queue, draft, pulse, model,
      modelError, chooseModel, lendInput, focusThread, tree, treeError, research,
      wikiVersion, projects, fixtures, projectsError,
      expanded, toggleDir, openPath, openResearch,
      readFile, authFetch, lock, beginDraft, newChat, showChat, openSession, forget,
      arming, reanalyze, analysisJobs, checkAnalysis, menuOpen, closeMenu, chatOpen, closeChat, accountOpen,
      openKeys, archiveOpen, raiseThread, lowerThread, raiseArchive, wide,
      topbarFloating, onStreamScroll,
      // Not the stable `useState` setters they used to be: both are bound to
      // whichever thread is in front, so a memo that kept the old pair would
      // type into the conversation you just left.
      setDraft, setPulse,
      threads, frontKey, showThread, closeThread,
    ],
  );

  return value;
}

/**
 * The shape, read off the hook rather than written out.
 *
 * Sixty fields is a lot to declare twice, and a hand-written interface beside a
 * hook this size is a list that silently stops matching. This cannot.
 */
type ConsoleValue = ReturnType<typeof useConsoleState>;

const Console = createContext<ConsoleValue | null>(null);

export function ConsoleProvider({ children }: { children: React.ReactNode }) {
  return <Console.Provider value={useConsoleState()}>{children}</Console.Provider>;
}

/** Read the shell's state from inside a route. Only ever called under the provider. */
export function useConsole(): ConsoleValue {
  const value = useContext(Console);
  if (!value) throw new Error('useConsole outside ConsoleProvider');
  return value;
}

